import React, { useState, useEffect, useRef } from 'react';
import { Card, Spinner, Table, Button, Badge, Pagination, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';

interface Daire {
  daireID: number;
  daireNo: string;
  takipNo: string;
  sozlesmeTakipNo?: string;
  projeID: number;
  blokID: number;
  katID: number;
  cepheID: number;
  manzaraID: number;
  daireTipID: number;
  daireDurumu: string;
  yataySira: number;
  listeFiyati?: number;
  netM2?: number;
  brutM2?: number;
  paraBirimiID?: number;
  aciklama?: string | null;
  proje: {
    projeID: number;
    projeAdi: string;
    takipNo?: string;
  };
  blok: {
    blokID: number;
    blokAdi: string;
  };
  kat: {
    katID: number;
    katAdi: string;
  };
  cephe: {
    cepheID: number;
    cepheAdi: string;
  };
  manzara?: {
    manzaraID: number;
    manzaraAdi: string;
  } | null;
  daireTipi: {
    daireTipID: number;
    tip: string;
  };
}

interface Proje {
  projeID: number;
  projeAdi: string;
}

interface Blok {
  blokID: number;
  blokAdi: string;
  projeID: number;
}

interface SunumBelge {
  sunumBelgeId: number;
  takipNo: string;
  aciklama: string;
  sunumBelgeTuru: string;
  sunumBelgeAdi: string;
}

interface SunumListeItem {
  sunumID: number;
  takipNo: string;
  cariAdSoyad: string | null;
  cariFirma: string | null;
  baslik: string | null;
  durum: string;
  daireler: { daireID: number }[];
}

interface DLGVDaire {
  gorunumDaireID: number;
  daireID: number;
  xYuzde: number;
  yYuzde: number;
  rxMin?: number | null;
  ryMin?: number | null;
  rxMax?: number | null;
  ryMax?: number | null;
}
interface DLGVGorunum {
  gorunumID: number;
  gorunumAdi: string;
  aciklama?: string;
  arkaplanGorsel?: string;
  daireler: DLGVDaire[];
}

const API_BASE_URL = (axiosInstance.defaults.baseURL ?? '').replace(/\/$/, '');

const DairelerListe: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [daireler, setDaireler] = useState<Daire[]>([]);
  const [filteredDaireler, setFilteredDaireler] = useState<Daire[]>([]);
  const [sozlesmeFiyatlari, setSozlesmeFiyatlari] = useState<Record<number, number>>({});
  const [sozlesmeMusterileri, setSozlesmeMusterileri] = useState<Record<number, string>>({});
  const [modalDaireOzellikler, setModalDaireOzellikler] = useState<Array<{ozellik: string; deger: string}>>([]);
  const [modalProjeOzellikler, setModalProjeOzellikler] = useState<Array<{ozellik: string; deger: string}>>([]);
  const [galeriGorseller, setGaleriGorseller] = useState<SunumBelge[]>([]);
  const [galeriBelgeler, setGaleriBelgeler] = useState<SunumBelge[]>([]);
  const [galeriVideolar, setGaleriVideolar] = useState<SunumBelge[]>([]);
  const [galeriSlideIndex, setGaleriSlideIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState<SunumBelge | null>(null);
  
  // Filter state'leri
  const [projeler, setProjeler] = useState<Proje[]>([]);
  const [bloklar, setBloklar] = useState<Blok[]>([]);
  const [filters, setFilters] = useState<{
    projeID: string;
    blokID: string;
    daireTipID: string;
    katIDler: number[];
    daireNo: string;
    daireDurumu: string[];
    minFiyat: string;
    maxFiyat: string;
    sortBy: string;
  }>({
    projeID: '',
    blokID: '',
    daireTipID: '',
    katIDler: [],
    daireNo: '',
    daireDurumu: ['Satışta'],
    minFiyat: '',
    maxFiyat: '',
    sortBy: '',
  });
  
  // Sayfalama state'leri
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchDaireler();
    fetchProjeler();
    fetchSozlesmeFiyatlari();
  }, []);

  // Filtreler değiştiğinde otomatik uygula
  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Filtre değiştiğinde ilk sayfaya dön
  }, [filters, daireler]);

  const fetchDaireler = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/Daireler?aktifMi=true');
      setDaireler(response.data);
    } catch (error) {
      console.error('Daireler yüklenirken hata oluştu:', error);
      toast.error(t('msg.islemHatasi'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSozlesmeFiyatlari = async () => {
    try {
      const response = await axiosInstance.get('/api/Sozlesmeler?aktifMi=true');
      const fiyatMap: Record<number, number> = {};
      const musteriMap: Record<number, string> = {};
      (response.data as any[]).forEach(s => {
        if (s.daire?.daireID) {
          if (s.sozlesmeFiyati != null) {
            fiyatMap[s.daire.daireID] = s.sozlesmeFiyati;
          }
          if (s.cari?.adiSoyadi) {
            musteriMap[s.daire.daireID] = s.cari.adiSoyadi;
          }
        }
      });
      setSozlesmeFiyatlari(fiyatMap);
      setSozlesmeMusterileri(musteriMap);
    } catch {
      // sessiz hata
    }
  };

  const fetchProjeler = async () => {
    try {
      const response = await axiosInstance.get('/api/Projeler');
      setProjeler(response.data);
    } catch (error) {
      console.error('Projeler yüklenirken hata oluştu:', error);
    }
  };

  const fetchBloklar = async (projeID: string) => {
    if (!projeID) {
      setBloklar([]);
      return;
    }
    
    try {
      const response = await axiosInstance.get(`/api/Bloklar/proje/${projeID}`);
      setBloklar(response.data);
    } catch (error) {
      console.error('Bloklar yüklenirken hata oluştu:', error);
      setBloklar([]);
    }
  };

  const formatFiyat = (val: string): string => {
    const raw = val.replace(/\D/g, '');
    return raw ? Number(raw).toLocaleString('tr-TR') : '';
  };

  const parseFiyat = (val: string): number => Number(val.replace(/\./g, '').replace(/,/g, ''));

  // Filtreleme fonksiyonu
  const applyFilters = () => {
    let filtered = [...daireler];

    // Proje filtresi
    if (filters.projeID) {
      filtered = filtered.filter(daire => daire.projeID.toString() === filters.projeID);
    }

    // Blok filtresi
    if (filters.blokID) {
      filtered = filtered.filter(daire => daire.blokID.toString() === filters.blokID);
    }

    // Daire tipi filtresi
    if (filters.daireTipID) {
      filtered = filtered.filter(daire => daire.daireTipi?.daireTipID?.toString() === filters.daireTipID);
    }

    // Kat filtresi
    if (filters.katIDler.length > 0) {
      filtered = filtered.filter(daire => filters.katIDler.includes(daire.katID));
    }

    // Daire No filtresi
    if (filters.daireNo.trim()) {
      const searchLower = filters.daireNo.toLowerCase().trim();
      filtered = filtered.filter(daire => 
        daire.daireNo.toLowerCase().includes(searchLower) ||
        daire.proje?.projeAdi?.toLowerCase().includes(searchLower) ||
        (sozlesmeMusterileri[daire.daireID] && sozlesmeMusterileri[daire.daireID].toLowerCase().includes(searchLower))
      );
    }

    // Durum filtresi
    if (filters.daireDurumu.length > 0) {
      filtered = filtered.filter(daire => filters.daireDurumu.includes(daire.daireDurumu));
    }

    // Fiyat aralığı filtresi
    if (filters.minFiyat) {
      filtered = filtered.filter(daire => (daire.listeFiyati || 0) >= parseFiyat(filters.minFiyat));
    }
    if (filters.maxFiyat) {
      filtered = filtered.filter(daire => (daire.listeFiyati || 0) <= parseFiyat(filters.maxFiyat));
    }

    // Sıralama
    if (filters.sortBy === 'fiyat-artan') {
      filtered.sort((a, b) => (a.listeFiyati || 0) - (b.listeFiyati || 0));
    } else if (filters.sortBy === 'fiyat-azalan') {
      filtered.sort((a, b) => (b.listeFiyati || 0) - (a.listeFiyati || 0));
    } else if (filters.sortBy === 'daire-no') {
      filtered.sort((a, b) => a.daireNo.localeCompare(b.daireNo, 'tr'));
    }

    setFilteredDaireler(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    } as typeof prev));

    // Proje değiştiğinde blokları yükle
    if (key === 'projeID') {
      setFilters(prev => ({ ...prev, blokID: '' })); // Blok seçimini sıfırla
      fetchBloklar(value);
    }
  };

  const clearFilters = () => {
    setFilters({
      projeID: '',
      blokID: '',
      daireTipID: '',
      katIDler: [],
      daireNo: '',
      daireDurumu: [],
      minFiyat: '',
      maxFiyat: '',
      sortBy: '',
    });
    setBloklar([]);
  };

  const handleKatToggle = (katID: number) => {
    setFilters(prev => {
      const already = prev.katIDler.includes(katID);
      return { ...prev, katIDler: already ? prev.katIDler.filter(id => id !== katID) : [...prev.katIDler, katID] };
    });
  };

  const handleDurumToggle = (value: string) => {
    if (value === '') {
      // "Tümü" butonuna basıldı — seçimi temizle
      setFilters(prev => ({ ...prev, daireDurumu: [] }));
      return;
    }
    setFilters(prev => {
      const already = prev.daireDurumu.includes(value);
      return { ...prev, daireDurumu: already ? prev.daireDurumu.filter(v => v !== value) : [...prev.daireDurumu, value] };
    });
  };

  const handleSozlesmeYap = (daire: Daire) => {
    // Daire bilgisini state'e kaydet ve sözleşme sayfasına yönlendir
    navigate('/satis-sozlesmesi', { state: { selectedDaire: daire } });
  };

  const getDurumBadge = (durum: string) => {
    const BADGE_STYLES: Record<string, { bg: string; color: string; dot: string; shadow: string; label: string }> = {
      'Satışta':    { bg: '#dcfce7', color: '#16a34a', dot: '#22c55e', shadow: 'rgba(34,197,94,0.25)',   label: 'Satışta' },
      'Rezerve':    { bg: '#fef9c3', color: '#ca8a04', dot: '#f59e0b', shadow: 'rgba(245,158,11,0.25)',  label: 'Rezerve' },
      'Sözleşme':   { bg: '#dbeafe', color: '#2563eb', dot: '#3b82f6', shadow: 'rgba(59,130,246,0.25)', label: 'Sözleşme' },
      'ArsaSahibi': { bg: '#f1f5f9', color: '#475569', dot: '#94a3b8', shadow: 'rgba(100,116,139,0.15)', label: 'Arsa Sahibi' },
      'Taşeron':    { bg: '#f0f9ff', color: '#0369a1', dot: '#0ea5e9', shadow: 'rgba(14,165,233,0.2)',   label: 'Taşeron' },
    };
    const s = BADGE_STYLES[durum] || { bg: '#f8fafc', color: '#475569', dot: '#94a3b8', shadow: 'transparent', label: durum };
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 12px', borderRadius: 9999,
        background: s.bg, color: s.color,
        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
        boxShadow: `0 0 0 1px ${s.dot}33, 0 2px 8px ${s.shadow}`,
        whiteSpace: 'nowrap' as const,
      }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
        {s.label}
      </span>
    );
  };

  const [viewMode, setViewMode] = useState<'kart' | 'tablo'>('kart');
  const [katDropdownOpen, setKatDropdownOpen] = useState(false);
  const katDropdownRef = useRef<HTMLDivElement>(null);
  const [durumDropdownOpen, setDurumDropdownOpen] = useState(false);
  const durumDropdownRef = useRef<HTMLDivElement>(null);
  const [kartGoster, setKartGoster] = useState({ fiyat: true, cephe: true, manzara: true, m2: true });
  const [kartGosterDropdownOpen, setKartGosterDropdownOpen] = useState(false);
  const kartGosterDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!kartGosterDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (kartGosterDropdownRef.current && !kartGosterDropdownRef.current.contains(e.target as Node)) {
        setKartGosterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [kartGosterDropdownOpen]);

  useEffect(() => {
    if (!katDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (katDropdownRef.current && !katDropdownRef.current.contains(e.target as Node)) {
        setKatDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [katDropdownOpen]);

  useEffect(() => {
    if (!durumDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (durumDropdownRef.current && !durumDropdownRef.current.contains(e.target as Node)) {
        setDurumDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [durumDropdownOpen]);
  const [colVisible, setColVisible] = useState({
    cephe: true,
    manzara: true,
    m2: true,
    fiyat: true,
    musteri: false,
    satisFiyati: false,
  });

  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [modalDaire, setModalDaire] = useState<Daire | null>(null);
  const [dlDetayTab, setDlDetayTab] = useState<'galeri' | 'gorunum'>('galeri');
  const [dlGorunumlar, setDlGorunumlar] = useState<DLGVGorunum[]>([]);
  const [dlGorunumLoading, setDlGorunumLoading] = useState(false);
  const [dlGorunumSelectedIdx, setDlGorunumSelectedIdx] = useState(0);
  const [dlGorunumHovered, setDlGorunumHovered] = useState<number | null>(null);
  const [dlSunumModal, setDlSunumModal] = useState(false);
  const [dlSunumlar, setDlSunumlar] = useState<SunumListeItem[]>([]);
  const [dlSunumYukleniyor, setDlSunumYukleniyor] = useState(false);
  const [dlSunumEkleniyor, setDlSunumEkleniyor] = useState<number | null>(null);
  const [dlSunumEklendi, setDlSunumEklendi] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (modalDaire?.takipNo) {
      axiosInstance.get(`/api/DaireOzellikler/daire/${modalDaire.takipNo}`)
        .then(res => setModalDaireOzellikler(res.data))
        .catch(() => setModalDaireOzellikler([]));
      axiosInstance.get(`/api/SunumBelgeleri/takip/${modalDaire.takipNo}`)
        .then(res => {
          const daireBelgeler: SunumBelge[] = res.data || [];
          setGaleriGorseller(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Görsel'));
          setGaleriBelgeler(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Belge'));
          setGaleriVideolar(daireBelgeler.filter(b => b.sunumBelgeTuru === 'Video'));
          setGaleriSlideIndex(0);
        }).catch(() => {
          setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]); setGaleriSlideIndex(0);
        });
    } else {
      setModalDaireOzellikler([]);
      setGaleriGorseller([]); setGaleriBelgeler([]); setGaleriVideolar([]);
    }
    // Reset görünüm tab
    setDlDetayTab('galeri');
    setDlGorunumlar([]);
    setDlGorunumSelectedIdx(0);
    setDlGorunumHovered(null);
    setDlSunumModal(false);
    setDlSunumlar([]);
    setDlSunumEklendi(new Set());
    if (modalDaire?.proje?.takipNo) {
      axiosInstance.get(`/api/ProjeOzellikler/proje/${modalDaire.proje.takipNo}`)
        .then(res => setModalProjeOzellikler(res.data))
        .catch(() => setModalProjeOzellikler([]));
    } else {
      setModalProjeOzellikler([]);
    }
  }, [modalDaire]);

  const openDlSunumaEkle = async () => {
    setDlSunumModal(true);
    setDlSunumYukleniyor(true);
    try {
      const res = await axiosInstance.get('/api/MusteriSunumlari');
      const aktif = (res.data as SunumListeItem[]).filter(s => s.durum === 'Taslak' || s.durum === 'Devam Ediyor');
      setDlSunumlar(aktif);
      if (modalDaire) {
        const ekliSet = new Set(aktif.filter(s => s.daireler.some(d => d.daireID === modalDaire.daireID)).map(s => s.sunumID));
        setDlSunumEklendi(ekliSet);
      }
    } catch { toast.error('Sunumlar yüklenemedi.'); }
    finally { setDlSunumYukleniyor(false); }
  };

  const handleDlSunumaEkle = async (sunumID: number) => {
    if (!modalDaire) return;
    setDlSunumEkleniyor(sunumID);
    try {
      await axiosInstance.post(`/api/MusteriSunumlari/${sunumID}/daireler`, [modalDaire.daireID]);
      setDlSunumEklendi(prev => new Set(Array.from(prev).concat(sunumID)));
      toast.success('Daire sunuma eklendi.');
    } catch { toast.error('Ekleme başarısız.'); }
    finally { setDlSunumEkleniyor(null); }
  };

  const [sozlesmeOzetDaire, setSozlesmeOzetDaire] = useState<Daire | null>(null);
  const [sozlesmeOzet, setSozlesmeOzet] = useState<any>(null);
  const [sozlesmeOdemeler, setSozlesmeOdemeler] = useState<any[]>([]);
  const [sozlesmeOzetLoading, setSozlesmeOzetLoading] = useState(false);

  const openSozlesmeOzet = async (daire: Daire) => {
    setSozlesmeOzetDaire(daire);
    setSozlesmeOzetLoading(true);
    setSozlesmeOzet(null);
    setSozlesmeOdemeler([]);
    try {
      const sozRes = await axiosInstance.get(`/api/Sozlesmeler/daire/${daire.daireID}`);
      setSozlesmeOzet(sozRes.data);
      if (sozRes.data?.sozlesmeTakipNo) {
        const odemeRes = await axiosInstance.get(`/api/Odemeler/takip/${sozRes.data.sozlesmeTakipNo}`);
        setSozlesmeOdemeler(odemeRes.data);
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setSozlesmeOzet(null);
      } else {
        toast.error(t('msg.islemHatasi'));
      }
    } finally {
      setSozlesmeOzetLoading(false);
    }
  };

  // jsPDF'in varsayılan fontu Türkçe'ye özgü karakterleri (ş,ğ,ı,İ,ö,ü,ç) düzgün
  // basamıyor; bu yüzden proje genelinde (KatKarsiligArsaDetay, TaksitTakipPaneli)
  // kullanılan aynı transliterasyon yaklaşımını izliyoruz.
  const tr2pdf = (s: string | null | undefined): string => (s ?? '')
    .replace(/ğ/g, 'g').replace(/Ğ/g, 'G')
    .replace(/ü/g, 'u').replace(/Ü/g, 'U')
    .replace(/ş/g, 's').replace(/Ş/g, 'S')
    .replace(/ı/g, 'i').replace(/İ/g, 'I')
    .replace(/ö/g, 'o').replace(/Ö/g, 'O')
    .replace(/ç/g, 'c').replace(/Ç/g, 'C')
    .replace(/₺/g, 'TL');

  // ── PDF Rapor Oluştur ──────────────────────────────────────────────
  const generatePDF = () => {
    const now = new Date();

    const projeAdi = filters.projeID
      ? (projeler.find(p => p.projeID.toString() === filters.projeID)?.projeAdi || '')
      : t('label.tumProjeler');
    const durumAdi = filters.daireDurumu.length > 0 ? filters.daireDurumu.join(', ') : 'Tum Durumlar';

    // Tablodaki "Sütunlar" seçimini (colVisible) birebir yansıtan dinamik kolon seti
    const isSold = (d: Daire) => ['Sözleşme', 'Rezerve', 'ArsaSahibi', 'Taşeron'].includes(d.daireDurumu);
    type Col = { label: string; align?: 'right' | 'center'; render: (d: Daire) => string };
    const cols: Col[] = [
      { label: 'Daire No', render: d => d.daireNo || '-' },
      { label: 'Proje', render: d => d.proje?.projeAdi || '-' },
      { label: 'Blok', render: d => d.blok?.blokAdi || '-' },
      { label: 'Kat', render: d => d.kat?.katAdi || '-' },
      { label: 'Daire Tipi', render: d => d.daireTipi?.tip || '-' },
    ];
    if (colVisible.cephe) cols.push({ label: 'Cephe', render: d => d.cephe?.cepheAdi || '-' });
    if (colVisible.manzara) cols.push({ label: 'Manzara', render: d => d.manzara?.manzaraAdi || '-' });
    if (colVisible.m2) {
      cols.push({ label: 'Net m2', align: 'right', render: d => d.netM2 != null ? d.netM2 + ' m2' : '-' });
      cols.push({ label: 'Brut m2', align: 'right', render: d => d.brutM2 != null ? d.brutM2 + ' m2' : '-' });
    }
    if (colVisible.fiyat) cols.push({ label: 'Liste Fiyati', align: 'right', render: d => d.listeFiyati != null ? d.listeFiyati.toLocaleString('tr-TR') + ' TL' : '-' });
    if (colVisible.satisFiyati) cols.push({ label: 'Satis Fiyati', align: 'right', render: d => isSold(d) && sozlesmeFiyatlari[d.daireID] != null ? sozlesmeFiyatlari[d.daireID].toLocaleString('tr-TR') + ' TL' : '-' });
    if (colVisible.musteri) cols.push({ label: 'Musteri', render: d => isSold(d) && sozlesmeMusterileri[d.daireID] ? sozlesmeMusterileri[d.daireID] : '-' });
    cols.push({ label: 'Durum', align: 'center', render: d => d.daireDurumu || '-' });

    const totalValue = filteredDaireler.reduce((sum, d) => sum + (d.listeFiyati || 0), 0);
    const fiyatColIdx = cols.findIndex(c => c.label === 'Liste Fiyati');

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 32;
    const headerH = 52;
    const topMargin = headerH + 16; // her sayfada (2. sayfa dahil) üstte tekrar eden boşluk
    const bottomMargin = 32; // her sayfada alt boşluk — satırların kesilmesini engeller

    // Başlık bandı — autoTable didDrawPage her sayfada bunu yeniden çizer
    const drawHeader = () => {
      doc.setFillColor(26, 35, 64);
      doc.rect(0, 0, pageWidth, headerH, 'F');
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(tr2pdf('Daire Listesi Raporu'), margin, 30);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(tr2pdf(`${projeAdi} - ${durumAdi} - ${filteredDaireler.length} daire`), margin, 44);
      doc.text(now.toLocaleDateString('tr-TR') + ' ' + now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }), pageWidth - margin, 30, { align: 'right' });
      doc.setTextColor(0);
    };

    autoTable(doc, {
      startY: topMargin,
      margin: { left: margin, right: margin, top: topMargin, bottom: bottomMargin },
      head: [['#', ...cols.map(c => tr2pdf(c.label))]],
      body: filteredDaireler.map((d, i) => [String(i + 1), ...cols.map(c => tr2pdf(c.render(d)))]),
      foot: fiyatColIdx >= 0 ? [[
        { content: 'Toplam Liste Degeri', colSpan: fiyatColIdx + 1, styles: { halign: 'left' } } as any,
        { content: tr2pdf(totalValue.toLocaleString('tr-TR') + ' TL'), styles: { halign: 'right' } } as any,
        ...(cols.length - fiyatColIdx - 1 > 0 ? [{ content: '', colSpan: cols.length - fiyatColIdx - 1 } as any] : []),
      ]] : undefined,
      showFoot: 'lastPage',
      showHead: 'everyPage',
      theme: 'grid',
      styles: { font: 'helvetica', fontSize: 8, cellPadding: 5, overflow: 'linebreak', valign: 'middle' },
      headStyles: { fillColor: [26, 35, 64], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
      footStyles: { fillColor: [230, 125, 33], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
      alternateRowStyles: { fillColor: [245, 247, 252] },
      columnStyles: Object.fromEntries(
        cols.map((c, i) => [i + 1, { halign: c.align === 'right' ? 'right' : c.align === 'center' ? 'center' : 'left' }])
      ),
      didDrawPage: drawHeader,
    });

    // Sayfa altı — sabit alt bilgi + sayfa numarası
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150);
      doc.text(`Sayfa ${i} / ${pageCount}`, pageWidth - margin, pageHeight - 12, { align: 'right' });
      doc.setTextColor(0);
    }

    doc.save(`Daire_Listesi_${now.toISOString().slice(0, 10)}.pdf`);
  };

  // Sayfalama fonksiyonları
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredDaireler.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredDaireler.length / itemsPerPage);
  };

  // ── Quick-stat counts ──────────────────────────────────────────────
  const statCounts = {
    toplam:    daireler.length,
    satista:   daireler.filter(d => d.daireDurumu === 'Satışta').length,
    rezerve:   daireler.filter(d => d.daireDurumu === 'Rezerve').length,
    sozlesme:  daireler.filter(d => d.daireDurumu === 'Sözleşme').length,
  };

  const DURUM_PILLS: { value: string; label: string; dot: string }[] = [
    { value: '', label: t('label.tumu'),        dot: '#94a3b8' },
    { value: 'Satışta', label: t('durum.satista'),     dot: '#22c55e' },
    { value: 'Rezerve', label: t('durum.rezerve'),     dot: '#f59e0b' },
    { value: 'Sözleşme', label: t('durum.sozlesme'),    dot: '#3b82f6' },
    { value: 'ArsaSahibi',label: 'Arsa Sahibi', dot: '#8b5cf6' },
    { value: 'Taşeron', label: t('durum.taseron'),     dot: '#0ea5e9' },
  ];

  const katSirasiHesapla = (katAdi: string): number => {
    if (/bodrum/i.test(katAdi)) return -1;
    if (/zemin/i.test(katAdi)) return 0;
    const m = katAdi.match(/\d+/);
    return m ? parseInt(m[0]) : 99;
  };

  const availableKatlar = (() => {
    let d = daireler;
    if (filters.projeID) d = d.filter(x => x.projeID.toString() === filters.projeID);
    if (filters.blokID) d = d.filter(x => x.blokID.toString() === filters.blokID);
    const seen = new Set<number>();
    const result: { katID: number; katAdi: string }[] = [];
    d.forEach(x => {
      if (!seen.has(x.katID)) {
        seen.add(x.katID);
        result.push({ katID: x.katID, katAdi: x.kat?.katAdi || String(x.katID) });
      }
    });
    return result.sort((a, b) => katSirasiHesapla(a.katAdi) - katSirasiHesapla(b.katAdi));
  })();

  const DURUM_CARD: Record<string, { bg: string; border: string; headerBg: string; tipColor: string; fiyatColor: string }> = {
    'Satışta':    { bg: '#f0fdf4', border: '#86efac', headerBg: '#dcfce7', tipColor: '#15803d', fiyatColor: '#059669' },
    'Rezerve':    { bg: '#fffbeb', border: '#fde68a', headerBg: '#fef9c3', tipColor: '#b45309', fiyatColor: '#d97706' },
    'Sözleşme':   { bg: '#eff6ff', border: '#bfdbfe', headerBg: '#dbeafe', tipColor: '#1d4ed8', fiyatColor: '#2563eb' },
    'ArsaSahibi': { bg: '#f8fafc', border: '#e2e8f0', headerBg: '#f1f5f9', tipColor: '#475569', fiyatColor: '#64748b' },
    'Taşeron':    { bg: '#f0f9ff', border: '#7dd3fc', headerBg: '#e0f2fe', tipColor: '#0369a1', fiyatColor: '#0284c7' },
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%',
            border: '3px solid #e2e8f0', borderTopColor: '#3b82f6',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
          }} />
          <span style={{ color: '#94a3b8', fontSize: 13 }}>{t('msg.yukleniyor')}</span>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="dl-page" style={{ background: '#F8F9FA', minHeight: '100vh', padding: '24px', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <style>{`
        .dl-card-row {
          background: #fff;
          border-radius: 12px;
          border: 1px solid #f0f2f5;
          box-shadow: 0 1px 3px rgba(15,23,42,0.04);
          padding: 10px 18px;
          display: flex;
          align-items: center;
          gap: 0;
          transition: box-shadow 0.18s, transform 0.18s, border-color 0.18s;
          position: relative;
        }
        .dl-card-row:hover {
          border-color: #f8cfa0 !important;
          box-shadow: 0 4px 16px rgba(205,105,6,0.10), 0 1px 4px rgba(15,23,42,0.06);
          transform: translateY(-1px);
        }
        .dl-pill-btn {
          padding: 6px 16px;
          border-radius: 9999px;
          border: 1.5px solid #e2e8f0;
          background: #fff;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }
        .dl-pill-btn:hover {
          border-color: #cbd5e1;
          background: #f8fafc;
        }
        .dl-pill-btn.active {
          background: #cd6906;
          border-color: #cd6906;
          color: #fff;
        }
        .dl-three-dot {
          width: 32px; height: 32px;
          border-radius: 8px;
          border: 1.5px solid #e2e8f0;
          background: #fff;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          line-height: 1;
          transition: background 0.15s, border-color 0.15s;
          position: relative;
        }
        .dl-three-dot:hover { background: #f1f5f9; border-color: #cbd5e1; }
        .dl-ctx-menu {
          position: absolute;
          right: 0;
          top: 38px;
          background: #fff;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(15,23,42,0.12);
          z-index: 999;
          min-width: 160px;
          overflow: hidden;
        }
        .dl-ctx-item {
          padding: 10px 16px;
          font-size: 12.5px;
          color: #334155;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .dl-ctx-item:hover { background: #f8fafc; }
        .dl-sozlesme-btn {
          padding: 7px 16px;
          border-radius: 8px;
          border: 1.5px solid #e2e8f0;
          background: #f8fafc;
          color: #374151;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          box-shadow: none;
          transition: background 0.15s, border-color 0.15s, color 0.15s;
          white-space: nowrap;
        }
        .dl-sozlesme-btn:hover { background: #f1f5f9; border-color: #cbd5e1; color: #111827; }
        .dl-search-wrap {
          position: relative;
          flex: 1;
          min-width: 220px;
          max-width: 320px;
        }
        .dl-search-input {
          width: 100%;
          padding: 9px 14px 9px 36px;
          border-radius: 10px;
          border: 1.5px solid #e2e8f0;
          background: #fff;
          font-size: 13px;
          color: #1e293b;
          box-shadow: inset 0 1.5px 4px rgba(15,23,42,0.05);
          outline: none;
          transition: border-color 0.15s;
        }
        .dl-search-input:focus { border-color: #94a3b8; }
        .dl-stat-card {
          border-radius: 14px;
          padding: 16px 22px;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 14px;
          flex: 1;
        }
        @keyframes dl-spin { to { transform: rotate(360deg); } }
        @keyframes dl-fadeup { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .dl-row-animate {
          animation: dl-fadeup 0.22s ease both;
        }
        .dl-divider { height: 1px; background: #f1f5f9; margin: 4px 0; }
        /* Modal */
        .dl-modal-overlay {
          position: fixed; inset: 0;
          background: rgba(15,23,42,0.45);
          backdrop-filter: blur(4px);
          z-index: 1050;
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
          animation: dl-fadein 0.18s ease;
        }
        @keyframes dl-fadein { from { opacity: 0; } to { opacity: 1; } }
        .dl-modal {
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 24px 80px rgba(15,23,42,0.18);
          width: 100%;
          max-width: 720px;
          max-height: calc(100vh - 48px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          animation: dl-fadeup 0.22s ease;
        }
        .dl-modal-header {
          padding: 22px 28px 18px;
          border-bottom: 1px solid #f1f5f9;
          display: flex; align-items: flex-start; justify-content: space-between;
          flex-shrink: 0;
        }
        .dl-modal-body {
          padding: 24px 28px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px 24px;
          overflow-y: auto;
          flex: 1;
          min-height: 0;
        }
        .dl-modal-field {
          display: flex; flex-direction: column; gap: 3px;
        }
        .dl-modal-label {
          font-size: 10px; font-weight: 700; color: #94a3b8;
          letter-spacing: 0.5px;
        }
        .dl-modal-value {
          font-size: 14px; font-weight: 700; color: #1e293b;
        }
        .dl-modal-footer {
          padding: 16px 28px 22px;
          display: flex; gap: 10px; justify-content: flex-end;
          border-top: 1px solid #f1f5f9;
          flex-shrink: 0;
        }
        .dl-close-btn {
          width: 32px; height: 32px; border-radius: 8px;
          border: 1.5px solid #e2e8f0; background: #f8fafc;
          color: #64748b; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 16px; transition: background 0.12s;
        }
        .dl-close-btn:hover { background: #f1f5f9; }

        /* ── Responsive ── */
        @media (max-width: 991px) {
          .dl-page { padding: 18px 14px !important; }
          .dl-card-row { flex-wrap: wrap; padding: 16px 16px; gap: 10px; }
          .dl-modal-body { grid-template-columns: 1fr; }
        }

        @media (max-width: 768px) {
          .dl-stat-card { min-width: calc(50% - 10px); flex: 0 0 calc(50% - 10px); }
          .dl-search-wrap { min-width: 100%; max-width: 100%; }
        }

        @media (max-width: 576px) {
          .dl-page { padding: 14px 8px !important; }
          .dl-stat-card { min-width: 100%; flex: 0 0 100%; }
        }
        .dl-sidebar {
          width: 250px;
          flex-shrink: 0;
          background: #fff;
          border-radius: 14px;
          border: 1.5px solid #f1f5f9;
          box-shadow: 0 1px 3px rgba(15,23,42,0.04);
          padding: 18px 16px;
          position: sticky;
          top: 24px;
          max-height: calc(100vh - 48px);
          overflow-y: auto;
        }
        .dl-sidebar-label {
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          margin-bottom: 6px;
          display: block;
        }
        .dl-sidebar-select {
          width: 100%;
          padding: 7px 10px;
          border-radius: 8px;
          border: 1.5px solid #e2e8f0;
          background: #f8fafc;
          font-size: 12.5px;
          color: #475569;
          outline: none;
          margin-bottom: 14px;
          box-sizing: border-box;
        }
        .dl-sidebar-section { margin-bottom: 18px; }
        .dl-sidebar-divider { height: 1px; background: #f1f5f9; margin: 14px 0; }
        .dl-filter-check {
          display: flex; align-items: center; gap: 8px; cursor: pointer;
          padding: 5px 8px; border-radius: 7px;
          font-size: 12.5px; font-weight: 600;
          user-select: none; transition: background 0.1s;
          margin-bottom: 2px;
        }
        .dl-filter-check:hover { background: #f8fafc; }
        .dl-daire-card {
          width: 140px;
          border-radius: 10px;
          overflow: hidden;
          cursor: pointer;
          transition: box-shadow 0.15s, transform 0.15s;
          flex-shrink: 0;
        }
        .dl-daire-card:hover {
          box-shadow: 0 4px 18px rgba(0,0,0,0.12);
          transform: translateY(-2px);
        }
        @media (max-width: 900px) {
          .dl-sidebar { width: 200px; }
          .dl-daire-card { width: 120px; }
        }
      `}</style>

      {/* ── PAGE HEADER ─────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 40, gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>{t('proje.yonetimi')}</h1>
          
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {/* Segmented view toggle – icon only */}
        <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 8, padding: 3, gap: 2 }}>
          {/* Kart */}
          <button
            onClick={() => setViewMode('kart')}
            title={t('label.kartGorunumu')}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
              background: viewMode === 'kart' ? '#fff' : 'transparent',
              color: viewMode === 'kart' ? '#cd6906' : '#94a3b8',
              boxShadow: viewMode === 'kart' ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
          </button>
          {/* Tablo */}
          <button
            onClick={() => setViewMode('tablo')}
            title={t('label.tabloGorunumu')}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer',
              background: viewMode === 'tablo' ? '#fff' : 'transparent',
              color: viewMode === 'tablo' ? '#cd6906' : '#94a3b8',
              boxShadow: viewMode === 'tablo' ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        </div>
        {/* Separator */}
        <div style={{ width: 1, height: 28, background: '#e2e8f0', flexShrink: 0 }} />
        <button
          onClick={() => navigate('/sozlesmeler')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0',
            background: '#fff', color: '#475569',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            cursor: 'pointer', whiteSpace: 'nowrap',
            boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
            transition: 'border-color 0.15s, background 0.15s, color 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.color = '#cd6906'; e.currentTarget.style.background = '#fff8f0'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = '#fff'; }}
          title="Sözleşmeler sayfasına geç"
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          {t('sozlesme.baslik')}
        </button>
        <button
          onClick={() => navigate('/satis-sozlesmesi')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 14px', borderRadius: 9, border: 'none',
            background: '#cd6906', color: '#fff',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            cursor: 'pointer', whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(205,105,6,0.25)',
            transition: 'background 0.15s, transform 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#b85d05'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.transform = 'translateY(0)'; }}
          title={t('sozlesme.yeni')}
        >
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          {t('btn.yeniSozlesme')}
        </button>
        </div>
      </div>

      {/* ── QUICK STATS (sadece tablo modunda) ─────────────────────────── */}
      {viewMode === 'tablo' && <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        {([
          { label: 'Toplam Daire', filterVal: '',          count: statCounts.toplam,   svgPath: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
          { label: 'Satışta',      filterVal: 'Satışta',   count: statCounts.satista,  svgPath: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z' },
          { label: 'Rezerve',      filterVal: 'Rezerve',   count: statCounts.rezerve,  svgPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
          { label: 'Sözleşme',     filterVal: 'Sözleşme',  count: statCounts.sozlesme, svgPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        ] as { label: string; filterVal: string; count: number; svgPath: string }[]).map(s => {
          const isActive = s.filterVal === ''
            ? filters.daireDurumu.length === 0
            : filters.daireDurumu.includes(s.filterVal);
          return (
            <div
              key={s.label}
              className="dl-stat-card"
              onClick={() => handleDurumToggle(s.filterVal)}
              style={{
                background: isActive ? '#cd6906' : '#f3f4f6',
                borderRadius: 8, cursor: 'pointer',
                transition: 'background 0.15s, box-shadow 0.15s',
                boxShadow: isActive ? '0 4px 16px rgba(205,105,6,0.25)' : 'none',
              }}
            >
              <span style={{
                width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                background: isActive ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={isActive ? '#fff' : '#64748b'} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d={s.svgPath} />
                </svg>
              </span>
              <span style={{ fontSize: 28, fontWeight: 800, color: isActive ? '#fff' : '#1e293b', lineHeight: 1, letterSpacing: '-0.04em', flexShrink: 0 }}>{s.count}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: isActive ? 'rgba(255,255,255,0.85)' : '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>{s.label}</span>
            </div>
          );
        })}
      </div>}

      {/* ── FILTER BAR (only in tablo mode) ──────────────────────────── */}
      {viewMode === 'tablo' && <div style={{
        background: '#fff', borderRadius: 14,
        border: '1.5px solid #f1f5f9',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
        padding: '10px 16px', marginBottom: 16,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}>
        {/* Row 1: Search + Dropdowns + Status pills + PDF */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><circle cx={11} cy={11} r={8}/><line x1={21} y1={21} x2={16.65} y2={16.65}/></svg>
            <input
              type="text"
              placeholder={t('label.daireAra')}
              value={filters.daireNo}
              onChange={e => handleFilterChange('daireNo', e.target.value)}
              style={{
                paddingLeft: 28, paddingRight: 10, paddingTop: 5, paddingBottom: 5,
                width: 196, borderRadius: 8, border: '1.5px solid #e2e8f0',
                background: '#f8fafc', fontSize: 12, color: '#334155', outline: 'none',
              }}
              onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.background = '#fff'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
            />
          </div>

          {/* Project select */}
          <select
            style={{
              padding: '5px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0',
              background: '#f8fafc', fontSize: 12, fontWeight: 600, color: '#475569',
              outline: 'none', cursor: 'pointer', flexShrink: 0,
            }}
            value={filters.projeID}
            onChange={e => handleFilterChange('projeID', e.target.value)}
          >
            <option value="">{t('label.tumProjeler')}</option>
            {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
          </select>

          {/* Block select */}
          <select
            style={{
              padding: '5px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0',
              background: filters.projeID ? '#f8fafc' : '#f8fafc',
              fontSize: 12, fontWeight: 600, color: filters.projeID ? '#475569' : '#cbd5e1',
              outline: 'none', cursor: filters.projeID ? 'pointer' : 'not-allowed', flexShrink: 0,
            }}
            value={filters.blokID}
            onChange={e => handleFilterChange('blokID', e.target.value)}
            disabled={!filters.projeID}
          >
            <option value="">{t('label.tumBloklar')}</option>
            {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
          </select>

          {/* Daire Tipi select */}
          {(() => {
            const tipler = Array.from(
              new Map(daireler.filter(d => d.daireTipi?.daireTipID).map(d => [d.daireTipi.daireTipID, d.daireTipi])).values()
            ).sort((a, b) => a.tip.localeCompare(b.tip));
            return tipler.length > 0 ? (
              <select
                style={{
                  padding: '5px 10px', borderRadius: 8,
                  border: `1.5px solid ${filters.daireTipID ? '#cd6906' : '#e2e8f0'}`,
                  background: '#f8fafc', fontSize: 12, fontWeight: 600,
                  color: filters.daireTipID ? '#cd6906' : '#475569',
                  outline: 'none', cursor: 'pointer', flexShrink: 0,
                }}
                value={filters.daireTipID}
                onChange={e => handleFilterChange('daireTipID', e.target.value)}
              >
                <option value="">Tüm Tipler</option>
                {tipler.map(tip => <option key={tip.daireTipID} value={tip.daireTipID}>{tip.tip}</option>)}
              </select>
            ) : null;
          })()}

          <div style={{ width: 1, height: 22, background: '#e2e8f0', flexShrink: 0 }} />

          {/* Status pill filters */}
          {DURUM_PILLS.map(pill => {
            const isActive = pill.value === ''
              ? filters.daireDurumu.length === 0
              : filters.daireDurumu.includes(pill.value);
            return (
              <button
                key={pill.value}
                onClick={() => handleDurumToggle(pill.value)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '4px 11px', borderRadius: 9999, flexShrink: 0,
                  fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                  border: isActive ? `1.5px solid ${pill.value === '' ? '#cd6906' : pill.dot}` : '1.5px solid #e2e8f0',
                  background: isActive ? (pill.value === '' ? '#cd6906' : pill.dot) : '#fff',
                  color: isActive ? '#fff' : '#64748b',
                  transition: 'all 0.12s',
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? 'rgba(255,255,255,0.8)' : pill.dot, flexShrink: 0 }} />
                {pill.label}
              </button>
            );
          })}

          {/* PDF — pushed right */}
          <button
            onClick={generatePDF}
            style={{
              marginLeft: 'auto', flexShrink: 0,
              padding: '5px 12px', borderRadius: 8,
              border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b',
              fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
              transition: 'all 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#cd6906'; e.currentTarget.style.color = '#cd6906'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
            title={t('btn.pdfIndir')}
          >
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1={12} y1={18} x2={12} y2={12}/>
              <polyline points="9 15 12 18 15 15"/>
            </svg>
            {t('btn.pdfIndir')}
          </button>
        </div>

        {/* Row 2: Price range + Sort + Clear (only shown when needed or always slim) */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Fiyat Aralığı */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <input
              type="text"
              placeholder="Min ₺"
              value={filters.minFiyat}
              onChange={e => handleFilterChange('minFiyat', formatFiyat(e.target.value))}
              style={{
                width: 100, padding: '5px 9px', borderRadius: 8,
                border: '1.5px solid #e2e8f0', background: '#f8fafc',
                fontSize: 12, color: '#475569', outline: 'none',
              }}
              onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.background = '#fff'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
            />
            <span style={{ color: '#cbd5e1', fontSize: 12, flexShrink: 0 }}>—</span>
            <input
              type="text"
              placeholder="Max ₺"
              value={filters.maxFiyat}
              onChange={e => handleFilterChange('maxFiyat', formatFiyat(e.target.value))}
              style={{
                width: 100, padding: '5px 9px', borderRadius: 8,
                border: '1.5px solid #e2e8f0', background: '#f8fafc',
                fontSize: 12, color: '#475569', outline: 'none',
              }}
              onFocus={e => { e.target.style.borderColor = '#cd6906'; e.target.style.background = '#fff'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
            />
          </div>

          <div style={{ width: 1, height: 20, background: '#e2e8f0', flexShrink: 0 }} />

          {/* Sıralama */}
          <select
            style={{
              padding: '5px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0',
              background: '#f8fafc', fontSize: 12, fontWeight: 600, color: '#475569',
              outline: 'none', cursor: 'pointer',
            }}
            value={filters.sortBy}
            onChange={e => handleFilterChange('sortBy', e.target.value)}
          >
            <option value="">{t('label.siralama')}</option>
            <option value="fiyat-artan">Fiyat ↑</option>
            <option value="fiyat-azalan">Fiyat ↓</option>
            <option value="daire-no">Daire No</option>
          </select>

          {/* Clear */}
          {(filters.daireNo || filters.projeID || filters.blokID || filters.daireTipID || filters.daireDurumu || filters.minFiyat || filters.maxFiyat || filters.sortBy) && (
            <button
              onClick={clearFilters}
              style={{
                padding: '4px 12px', borderRadius: 9999,
                border: '1.5px solid #fca5a5', background: '#fff', color: '#dc2626',
                fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round"><line x1={18} y1={6} x2={6} y2={18}/><line x1={6} y1={6} x2={18} y2={18}/></svg>
              {t('btn.temizle')}
            </button>
          )}
        </div>
      </div>}

      {/* ── RESULTS LABEL (tablo only) ───────────────────────── */}
      {viewMode === 'tablo' && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: 12.5, color: '#94a3b8', fontWeight: 600 }}>
          {filteredDaireler.length} {t('tablo.kayitListeleniyor')}
          {filteredDaireler.length !== daireler.length && ` (${daireler.length} toplam)`}
        </span>
        <span style={{ fontSize: 12, color: '#cbd5e1' }}>
          {t('tablo.sayfa')} {currentPage} / {getTotalPages() || 1}
        </span>
      </div>}

      {/* ── COLUMN VISIBILITY CHECKBOXES (tablo modunda) ── */}
      {viewMode === 'tablo' && (
        <div style={{
          background: '#fff', borderRadius: 10, border: '1.5px solid #f1f5f9',
          padding: '8px 16px', marginBottom: 10,
          display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', marginRight: 8, whiteSpace: 'nowrap' }}>{t('label.sutunlar')}</span>
          {([
            { key: 'cephe',      label: t('daire.cephe') },
            { key: 'manzara',    label: t('daire.manzara') },
            { key: 'm2',         label: 'm²' },
            { key: 'fiyat',      label: t('label.fiyat') },
            { key: 'musteri',    label: t('label.musteri') },
            { key: 'satisFiyati',label: t('daire.satisFiyati') },
          ] as { key: keyof typeof colVisible; label: string }[]).map(col => {
            const checked = colVisible[col.key];
            const isSales = col.key === 'musteri' || col.key === 'satisFiyati';
            return (
              <label key={col.key} onClick={() => setColVisible(prev => ({ ...prev, [col.key]: !prev[col.key] }))}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  padding: '4px 10px', borderRadius: 9999,
                  border: `1.5px solid ${checked ? (isSales ? '#3b82f6' : '#cd6906') : '#e2e8f0'}`,
                  background: checked ? (isSales ? '#eff6ff' : '#fff8f0') : '#f8fafc',
                  fontSize: 12, fontWeight: 600,
                  color: checked ? (isSales ? '#2563eb' : '#cd6906') : '#94a3b8',
                  userSelect: 'none', transition: 'all 0.12s',
                }}>
                <span style={{
                  width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                  border: `1.5px solid ${checked ? (isSales ? '#3b82f6' : '#cd6906') : '#cbd5e1'}`,
                  background: checked ? (isSales ? '#3b82f6' : '#cd6906') : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {checked && <svg width={8} height={8} viewBox="0 0 10 10" fill="none"><polyline points="1.5,5.5 4,8 8.5,2" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </span>
                {col.label}
              </label>
            );
          })}
        </div>
      )}

      {/* ── TABLE VIEW ──────────────────────────── */}
      {viewMode === 'tablo' && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #f1f5f9', overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }} onClick={() => setOpenMenuId(null)}>
          {getCurrentPageItems().length === 0 ? (
            <div style={{ padding: '56px 24px', textAlign: 'center' }}>
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/></svg></div>
              <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>{t('tablo.daireYok')}</div>
              <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>{t('tablo.filtreDegistir')}</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#1a2340' }}>
                    <th style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>#</th>
                    <th style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.daireNo')}</th>
                    <th style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('label.proje')}</th>
                    <th style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.blok')}</th>
                    <th style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.kat')}</th>
                    <th style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.tip')}</th>
                    {colVisible.cephe      && <th style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.cephe')}</th>}
                    {colVisible.manzara    && <th style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.manzara')}</th>}
                    {colVisible.m2         && <th style={{ padding: '11px 14px', textAlign: 'right', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.netM2')}</th>}
                    {colVisible.m2         && <th style={{ padding: '11px 14px', textAlign: 'right', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.brutM2')}</th>}
                    {colVisible.fiyat      && <th style={{ padding: '11px 14px', textAlign: 'right', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.listeFiyati')}</th>}
                    {colVisible.satisFiyati && <th style={{ padding: '11px 14px', textAlign: 'right', color: '#dbeafe', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('daire.satisFiyati')}</th>}
                    {colVisible.musteri    && <th style={{ padding: '11px 14px', textAlign: 'left', color: '#dbeafe', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('label.musteri')}</th>}
                    <th style={{ padding: '11px 14px', textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('label.durum')}</th>
                    <th style={{ padding: '11px 14px', textAlign: 'center', color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{t('label.islemler')}</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentPageItems().map((daire, idx) => {
                    const isSold = ['Sözleşme', 'Rezerve', 'ArsaSahibi', 'Taşeron'].includes(daire.daireDurumu);
                    const rowBg = idx % 2 === 0 ? '#fff' : '#f8fafc';
                    return (
                      <tr key={daire.daireID} style={{ background: rowBg, transition: 'background 0.12s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = '#fff8f0'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = rowBg; }}>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#94a3b8', fontSize: 12 }}>
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ fontWeight: 800, fontSize: 14, color: '#0f172a', letterSpacing: '-0.02em' }}>{daire.daireNo}</span>
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#475569', fontWeight: 500, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {daire.proje?.projeAdi || '—'}
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#475569', fontWeight: 600 }}>
                          {daire.blok?.blokAdi || '—'}
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#475569', fontWeight: 600 }}>
                          {daire.kat?.katAdi || '—'}
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>
                          {daire.daireTipi?.tip || '—'}
                        </td>
                        {colVisible.cephe && (
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>
                            {daire.cephe?.cepheAdi || '—'}
                          </td>
                        )}
                        {colVisible.manzara && (
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', color: '#334155' }}>
                            {daire.manzara?.manzaraAdi || '—'}
                          </td>
                        )}
                        {colVisible.m2 && (
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
                            {daire.netM2 != null ? `${daire.netM2}` : '—'}
                          </td>
                        )}
                        {colVisible.m2 && (
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
                            {daire.brutM2 != null ? `${daire.brutM2}` : '—'}
                          </td>
                        )}
                        {colVisible.fiyat && (
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700, color: '#059669', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {daire.listeFiyati != null ? `₺${daire.listeFiyati.toLocaleString('tr-TR')}` : '—'}
                          </td>
                        )}
                        {colVisible.satisFiyati && (
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                            color: isSold && sozlesmeFiyatlari[daire.daireID] != null ? '#2563eb' : '#cbd5e1' }}>
                            {isSold && sozlesmeFiyatlari[daire.daireID] != null
                              ? `₺${sozlesmeFiyatlari[daire.daireID].toLocaleString('tr-TR')}`
                              : '—'}
                          </td>
                        )}
                        {colVisible.musteri && (
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', fontWeight: 600,
                            color: isSold && sozlesmeMusterileri[daire.daireID] ? '#1d4ed8' : '#cbd5e1' }}>
                            {isSold && sozlesmeMusterileri[daire.daireID] ? sozlesmeMusterileri[daire.daireID] : '—'}
                          </td>
                        )}
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                          {getDurumBadge(daire.daireDurumu)}
                        </td>
                        <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>
                          <div style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
                            <button
                              className="dl-three-dot"
                              onClick={() => setOpenMenuId(openMenuId === daire.daireID ? null : daire.daireID)}
                              title={t('label.islemler')}
                            >
                              <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor"><circle cx={12} cy={5} r={1.8}/><circle cx={12} cy={12} r={1.8}/><circle cx={12} cy={19} r={1.8}/></svg>
                            </button>
                            {openMenuId === daire.daireID && (
                              <div className="dl-ctx-menu" style={{ right: 0, left: 'auto' }}>
                                <div className="dl-ctx-item" onClick={() => { setModalDaire(daire); setOpenMenuId(null); }}>
                                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx={12} cy={12} r={3}/></svg>
                                  {t('btn.daireDetayi')}
                                </div>
                                {isSold && (
                                  <>
                                    <div className="dl-divider" />
                                    <div className="dl-ctx-item" style={{ color: '#2563eb' }} onClick={() => { openSozlesmeOzet(daire); setOpenMenuId(null); }}>
                                      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1={16} y1={13} x2={8} y2={13}/><line x1={16} y1={17} x2={8} y2={17}/></svg>
                                      {t('btn.sozlesmeOzeti')}
                                    </div>
                                  </>
                                )}
                                <div className="dl-divider" />
                                {daire.daireDurumu === 'Satışta' ? (
                                  <div className="dl-ctx-item" style={{ color: '#15803d' }} onClick={() => { handleSozlesmeYap(daire); setOpenMenuId(null); }}>
                                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1={12} y1={18} x2={12} y2={12}/><line x1={9} y1={15} x2={15} y2={15}/></svg>
                                    {t('btn.sozlesmeOlustur')}
                                  </div>
                                ) : (
                                  <div className="dl-ctx-item" style={{ color: '#94a3b8', cursor: 'default' }}>
                                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    {t('btn.sozlesmeOlustur')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── KART GÖRÜNÜMÜ: Sidebar + Blok Grid ──────────────────────── */}
      {viewMode === 'kart' && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

          {/* ── SOL SIDEBAR: Filtreler ── */}
          <div className="dl-sidebar">
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.6px', marginBottom: 16, textTransform: 'uppercase' as const }}>{t('label.filtreler')}</div>

            {/* Proje */}
            <div className="dl-sidebar-section">
              <label className="dl-sidebar-label">{t('label.proje')}</label>
              <select className="dl-sidebar-select" value={filters.projeID} onChange={e => handleFilterChange('projeID', e.target.value)}>
                <option value="">{t('label.tumProjeler')}</option>
                {projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}
              </select>
            </div>

            {/* Blok */}
            <div className="dl-sidebar-section">
              <label className="dl-sidebar-label">{t('daire.blok')}</label>
              <select className="dl-sidebar-select" value={filters.blokID}
                onChange={e => handleFilterChange('blokID', e.target.value)}
                disabled={!filters.projeID}
                style={{ color: filters.projeID ? '#475569' : '#cbd5e1', cursor: filters.projeID ? 'pointer' : 'not-allowed' }}>
                <option value="">{t('label.tumBloklar')}</option>
                {bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
              </select>
            </div>

            {/* Daire Tipi */}
            {(() => {
              const tipler = Array.from(
                new Map(daireler.filter(d => d.daireTipi?.daireTipID).map(d => [d.daireTipi.daireTipID, d.daireTipi])).values()
              ).sort((a, b) => a.tip.localeCompare(b.tip));
              return tipler.length > 0 ? (
                <div className="dl-sidebar-section">
                  <label className="dl-sidebar-label">Daire Tipi</label>
                  <select className="dl-sidebar-select"
                    value={filters.daireTipID}
                    onChange={e => handleFilterChange('daireTipID', e.target.value)}
                    style={{ color: filters.daireTipID ? '#cd6906' : '#475569' }}>
                    <option value="">Tüm Tipler</option>
                    {tipler.map(tip => <option key={tip.daireTipID} value={tip.daireTipID}>{tip.tip}</option>)}
                  </select>
                </div>
              ) : null;
            })()}

            {/* Kat (multi) — açılır kutu */}
            {availableKatlar.length > 0 && (
              <div className="dl-sidebar-section" style={{ position: 'relative' }} ref={katDropdownRef}>
                <label className="dl-sidebar-label">{t('daire.kat')}</label>
                <button
                  onClick={() => setKatDropdownOpen(o => !o)}
                  style={{
                    width: '100%', padding: '7px 10px', borderRadius: 8,
                    border: `1.5px solid ${katDropdownOpen || filters.katIDler.length > 0 ? '#cd6906' : '#e2e8f0'}`,
                    background: '#f8fafc', fontSize: 12.5, color: '#475569',
                    outline: 'none', cursor: 'pointer', boxSizing: 'border-box' as const,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                    fontFamily: 'inherit', fontWeight: 600,
                  }}>
                  <span style={{ color: filters.katIDler.length > 0 ? '#cd6906' : '#94a3b8' }}>
                    {filters.katIDler.length > 0 ? `${filters.katIDler.length} kat seçili` : 'Tüm Katlar'}
                  </span>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"
                    style={{ transform: katDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {katDropdownOpen && (
                  <div style={{
                    position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 4,
                    background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(15,23,42,0.12)', zIndex: 50,
                    maxHeight: 220, overflowY: 'auto', padding: '6px 4px',
                  }}>
                  {availableKatlar.length > 0 && (
                    <label className="dl-filter-check"
                      onClick={() => setFilters(prev => ({ ...prev, katIDler: prev.katIDler.length === availableKatlar.length ? [] : availableKatlar.map(k => k.katID) }))}
                      style={{ color: '#64748b', borderBottom: '1px solid #f1f5f9', marginBottom: 4, paddingBottom: 6 }}>
                      <span style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        border: `1.5px solid ${filters.katIDler.length === availableKatlar.length ? '#cd6906' : '#cbd5e1'}`,
                        background: filters.katIDler.length === availableKatlar.length ? '#cd6906' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {filters.katIDler.length === availableKatlar.length && <svg width={8} height={8} viewBox="0 0 10 10"><polyline points="1.5,5.5 4,8 8.5,2" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </span>
                      {t('label.tumunuSec')}
                    </label>
                  )}
                  {availableKatlar.map(kat => {
                    const checked = filters.katIDler.includes(kat.katID);
                    return (
                      <label key={kat.katID} className="dl-filter-check"
                        onClick={() => handleKatToggle(kat.katID)}
                        style={{ color: checked ? '#cd6906' : '#475569', background: checked ? '#fff8f0' : undefined }}>
                        <span style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0, border: `1.5px solid ${checked ? '#cd6906' : '#cbd5e1'}`,
                          background: checked ? '#cd6906' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {checked && <svg width={8} height={8} viewBox="0 0 10 10"><polyline points="1.5,5.5 4,8 8.5,2" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </span>
                        {kat.katAdi}
                      </label>
                    );
                  })}
                  </div>
                )}
              </div>
            )}

            <div className="dl-sidebar-divider" />

            {/* Durum (multi) — açılır kutu */}
            <div className="dl-sidebar-section" style={{ position: 'relative' }} ref={durumDropdownRef}>
              <label className="dl-sidebar-label">{t('label.durum')}</label>
              <button
                onClick={() => setDurumDropdownOpen(o => !o)}
                style={{
                  width: '100%', padding: '7px 10px', borderRadius: 8,
                  border: `1.5px solid ${durumDropdownOpen || filters.daireDurumu.length > 0 ? '#cd6906' : '#e2e8f0'}`,
                  background: '#f8fafc', fontSize: 12.5, color: '#475569',
                  outline: 'none', cursor: 'pointer', boxSizing: 'border-box' as const,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                  fontFamily: 'inherit', fontWeight: 600,
                }}>
                <span style={{ color: filters.daireDurumu.length > 0 ? '#cd6906' : '#94a3b8' }}>
                  {filters.daireDurumu.length > 0 ? filters.daireDurumu.join(', ') : 'Tüm Durumlar'}
                </span>
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"
                  style={{ transform: durumDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              {durumDropdownOpen && (
                <div style={{
                  position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 4,
                  background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(15,23,42,0.12)', zIndex: 50,
                  padding: '6px 4px',
                }}>
                  {DURUM_PILLS.filter(p => p.value !== '').map(pill => {
                    const checked = filters.daireDurumu.includes(pill.value);
                    return (
                      <label key={pill.value} className="dl-filter-check"
                        onClick={() => handleDurumToggle(pill.value)}
                        style={{ color: checked ? pill.dot : '#475569' }}>
                        <span style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0, border: `1.5px solid ${checked ? pill.dot : '#cbd5e1'}`,
                          background: checked ? pill.dot : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {checked && <svg width={8} height={8} viewBox="0 0 10 10"><polyline points="1.5,5.5 4,8 8.5,2" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: pill.dot, flexShrink: 0 }} />
                          {pill.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="dl-sidebar-divider" />

            {/* Fiyat Aralığı */}
            <div className="dl-sidebar-section">
              <label className="dl-sidebar-label">{t('label.fiyatAraligi')}</label>
              <input type="text" placeholder="Min ₺" value={filters.minFiyat}
                onChange={e => handleFilterChange('minFiyat', formatFiyat(e.target.value))}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12.5, color: '#475569', outline: 'none', boxSizing: 'border-box' as const, marginBottom: 6 }} />
              <input type="text" placeholder="Max ₺" value={filters.maxFiyat}
                onChange={e => handleFilterChange('maxFiyat', formatFiyat(e.target.value))}
                style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', fontSize: 12.5, color: '#475569', outline: 'none', boxSizing: 'border-box' as const }} />
            </div>

            <div className="dl-sidebar-divider" />

            {/* Kart Görünümü — açılır kutu */}
            <div className="dl-sidebar-section" style={{ position: 'relative' }} ref={kartGosterDropdownRef}>
              <button onClick={() => setKartGosterDropdownOpen(o => !o)} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#fff', border: `1.5px solid ${kartGosterDropdownOpen ? '#1a2340' : '#e2e8f0'}`,
                borderRadius: 8, padding: '7px 10px', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#1a2340'
              }}>
                <span>{t('label.kartGorunumu')}</span>
                <svg width={13} height={13} viewBox="0 0 10 10" fill="none"
                  style={{ transform: kartGosterDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
                  <polyline points="1.5,3.5 5,7 8.5,3.5" stroke="#1a2340" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {kartGosterDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                  background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.10)', padding: '6px 4px', marginTop: 4
                }}>
                  {([
                    { key: 'fiyat',   label: 'Fiyat' },
                    { key: 'cephe',   label: 'Cephe' },
                    { key: 'manzara', label: 'Manzara' },
                    { key: 'm2',      label: 'm²' },
                  ] as { key: keyof typeof kartGoster; label: string }[]).map(({ key, label }) => {
                    const checked = kartGoster[key];
                    return (
                      <label key={key} className="dl-filter-check"
                        onClick={() => setKartGoster(prev => ({ ...prev, [key]: !prev[key] }))}
                        style={{ color: checked ? '#1a2340' : '#94a3b8' }}>
                        <span style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                          border: `1.5px solid ${checked ? '#1a2340' : '#cbd5e1'}`,
                          background: checked ? '#1a2340' : '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {checked && <svg width={8} height={8} viewBox="0 0 10 10"><polyline points="1.5,5.5 4,8 8.5,2" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"/></svg>}
                        </span>
                        {label}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="dl-sidebar-divider" />

            <button onClick={clearFilters}
              style={{ width: '100%', padding: '8px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#64748b', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              {t('btn.temizle')}
            </button>
            <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>
              {filteredDaireler.length} {t('tablo.daireGosteriliyor')}
            </div>
          </div>

          {/* ── SAĞ: Blok Grid ── */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {filteredDaireler.length === 0 ? (
              <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #f1f5f9', padding: '56px 24px', textAlign: 'center' }}>
                <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>{t('tablo.daireYok')}</div>
                <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>{t('tablo.solFiltreler')}</div>
              </div>
            ) : (() => {
              // Blok → Kat → Daire gruplama
              const blokMap = new Map<number, { blokAdi: string; katMap: Map<number, { katAdi: string; daireler: Daire[] }> }>();
              filteredDaireler.forEach(d => {
                if (!blokMap.has(d.blokID)) blokMap.set(d.blokID, { blokAdi: d.blok?.blokAdi || String(d.blokID), katMap: new Map() });
                const be = blokMap.get(d.blokID)!;
                if (!be.katMap.has(d.katID)) be.katMap.set(d.katID, { katAdi: d.kat?.katAdi || String(d.katID), daireler: [] });
                be.katMap.get(d.katID)!.daireler.push(d);
              });

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {Array.from(blokMap.entries()).map(([blokID, blokData]) => {
                    const katEntries = Array.from(blokData.katMap.entries())
                      .sort(([, a], [, b]) => katSirasiHesapla(b.katAdi) - katSirasiHesapla(a.katAdi));

                    return (
                      <div key={blokID}>
                        {blokMap.size > 1 && (
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2340', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 7 }}>
                            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                            {blokData.blokAdi}
                          </div>
                        )}
                        <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #f1f5f9', overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,0.04)' }}>
                          {katEntries.map(([katID, katData], katIdx) => {
                            const bosCount = katData.daireler.filter(d => d.daireDurumu === 'Satışta').length;
                            const dairesSorted = [...katData.daireler].sort((a, b) => (a.yataySira || 0) - (b.yataySira || 0));
                            return (
                              <div key={katID} style={{ display: 'flex', borderBottom: katIdx < katEntries.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                {/* Kat başlığı */}
                                <div style={{ width: 88, flexShrink: 0, padding: '12px 10px', background: '#f8fafc', borderRight: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                  <div style={{ fontSize: 12, fontWeight: 800, color: '#1a2340', lineHeight: 1.2 }}>{katData.katAdi}</div>
                                  <div style={{ fontSize: 10.5, fontWeight: 600, marginTop: 3, color: bosCount > 0 ? '#16a34a' : '#94a3b8' }}>
                                    {bosCount}/{katData.daireler.length} Boş
                                  </div>
                                </div>
                                {/* Daireler */}
                                <div style={{ flex: 1, padding: '10px 12px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                  {dairesSorted.map(daire => {
                                    const cs = DURUM_CARD[daire.daireDurumu] || DURUM_CARD['ArsaSahibi'];
                                    const cepheAdi = daire.cephe?.cepheAdi || '';
                                    const manzaraAdi = daire.manzara?.manzaraAdi || '';
                                    const showFooter = kartGoster.fiyat && daire.listeFiyati != null;
                                    return (
                                      <div key={daire.daireID} className="dl-daire-card"
                                        style={{ border: `1.5px solid ${cs.border}`, background: cs.bg }}
                                        onClick={() => setModalDaire(daire)}>
                                        {/* Kart başlığı */}
                                        <div style={{ background: cs.headerBg, padding: '5px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                                          <span style={{ fontSize: 11, fontWeight: 800, color: cs.tipColor, flexShrink: 0 }}>D:{daire.daireNo}</span>
                                          {kartGoster.cephe && cepheAdi && (
                                            <span style={{ fontSize: 10, fontWeight: 700, color: cs.tipColor, background: 'rgba(255,255,255,0.55)', borderRadius: 4, padding: '1px 5px', letterSpacing: '0.3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{cepheAdi}</span>
                                          )}
                                        </div>
                                        {/* Kart içeriği */}
                                        <div style={{ padding: '7px 8px' }}>
                                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
                                            {daire.daireTipi?.tip && (
                                              <div style={{ fontSize: 11, fontWeight: 700, color: cs.tipColor }}>
                                                {daire.daireTipi.tip}
                                              </div>
                                            )}
                                            {kartGoster.m2 && daire.netM2 != null && (
                                              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, flexShrink: 0 }}>{daire.netM2} m²</div>
                                            )}
                                          </div>
                                          {kartGoster.manzara && manzaraAdi && (
                                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{manzaraAdi}</div>
                                          )}
                                        </div>
                                        {/* Kart alt — fiyat */}
                                        {showFooter && (
                                          <div style={{ padding: '5px 8px 7px', borderTop: `1px dashed ${cs.border}`, textAlign: 'right' }}>
                                            <span style={{ fontSize: 11.5, fontWeight: 800, color: cs.fiyatColor, fontVariantNumeric: 'tabular-nums' }}>
                                              ₺{daire.listeFiyati!.toLocaleString('tr-TR', { minimumFractionDigits: 0 })}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── OLD CARD ROWS (hidden, kept for reference) ─────────────────── */}
      {(false) && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} onClick={() => setOpenMenuId(null)}>
        {getCurrentPageItems().length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: 16, border: '1.5px solid #f1f5f9',
            padding: '56px 24px', textAlign: 'center',
          }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 22V12h6v10"/><path d="M8 7h.01M12 7h.01M16 7h.01M8 11h.01M16 11h.01"/></svg></div>
            <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 16 }}>Daire bulunamadı</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Filtreleri değiştirerek tekrar deneyin.</div>
          </div>
        ) : getCurrentPageItems().map((daire, idx) => (
          <div
            key={daire.daireID}
            className="dl-card-row dl-row-animate"
            style={{ animationDelay: `${idx * 0.03}s`, zIndex: openMenuId === daire.daireID ? 10 : 'auto' as any }}
          >
            {/* ── Col 1: Unit Identity — compact fixed */}
            <div style={{ width: 104, flexShrink: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>
                {daire.daireNo}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginTop: 3, letterSpacing: '0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {daire.proje?.projeAdi || '—'}
              </div>
            </div>

            {/* ── Divider */}
            <div style={{ width: 1, background: '#f1f5f9', alignSelf: 'stretch', margin: '0 14px', flexShrink: 0 }} />

            {/* ── Col 2: Compact Details — single-line chips */}
            <div style={{ display: 'flex', gap: 4, flex: 1, alignItems: 'center', flexWrap: 'wrap', rowGap: 4 }}>
              {/* Blok */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: '#f8fafc', border: '1px solid #edf0f4', flexShrink: 0 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: '#b0bac5', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>Blok</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>{daire.blok?.blokAdi || '—'}</span>
              </span>
              {/* Kat */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: '#f8fafc', border: '1px solid #edf0f4', flexShrink: 0 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: '#b0bac5', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>Kat</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>{daire.kat?.katAdi || '—'}</span>
              </span>
              {/* Cephe */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: '#f8fafc', border: '1px solid #edf0f4', flexShrink: 0 }}>
                <span style={{ fontSize: 9, fontWeight: 600, color: '#b0bac5', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>Cephe</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>{daire.cephe?.cepheAdi || '—'}</span>
              </span>
              {/* Manzara */}
              {daire.manzara?.manzaraAdi && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: '#f8fafc', border: '1px solid #edf0f4', flexShrink: 0 }}>
                  <span style={{ fontSize: 9, fontWeight: 600, color: '#b0bac5', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>Manzara</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>{daire.manzara.manzaraAdi}</span>
                </span>
              )}
              {/* Tip */}
              {daire.daireTipi?.tip && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: '#f8fafc', border: '1px solid #edf0f4', flexShrink: 0 }}>
                  <span style={{ fontSize: 9, fontWeight: 600, color: '#b0bac5', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>Tip</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#334155' }}>{daire.daireTipi.tip}</span>
                </span>
              )}
              {/* m² */}
              {(daire.netM2 != null || daire.brutM2 != null) && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: '#f8fafc', border: '1px solid #edf0f4', flexShrink: 0 }}>
                  <span style={{ fontSize: 9, fontWeight: 600, color: '#b0bac5', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>m²</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
                    {[daire.netM2 != null ? `N:${daire.netM2}` : null, daire.brutM2 != null ? `B:${daire.brutM2}` : null].filter(Boolean).join(' / ')}
                  </span>
                </span>
              )}
              {/* Müşteri */}
              {['Sözleşme', 'Rezerve', 'ArsaSahibi', 'Taşeron'].includes(daire.daireDurumu) && sozlesmeMusterileri[daire.daireID] && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe', flexShrink: 0 }}>
                  <span style={{ fontSize: 9, fontWeight: 600, color: '#93c5fd', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>Müşteri</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8' }}>{sozlesmeMusterileri[daire.daireID]}</span>
                </span>
              )}
              {/* Liste Fiyatı — slightly prominent chip */}
              {daire.listeFiyati != null && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 7px', borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', flexShrink: 0 }}>
                  <span style={{ fontSize: 9, fontWeight: 600, color: '#86efac', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>Fiyat</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#059669', fontVariantNumeric: 'tabular-nums' }}>
                    ₺{daire.listeFiyati.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                  {daire.daireDurumu === 'Sözleşme' && sozlesmeFiyatlari[daire.daireID] != null && (
                    <>
                      <span style={{ width: 1, height: 12, background: '#bbf7d0', flexShrink: 0 }} />
                      <span style={{ fontSize: 9, fontWeight: 600, color: '#93c5fd', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>Satış</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', fontVariantNumeric: 'tabular-nums' }}>
                        ₺{sozlesmeFiyatlari[daire.daireID].toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </>
                  )}
                </span>
              )}
            </div>

            {/* ── Col 3: Status Badge */}
            <div style={{ margin: '0 10px', flexShrink: 0 }}>
              {getDurumBadge(daire.daireDurumu)}
            </div>

            {/* ── Col 4: Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {daire.daireDurumu === 'Satışta' && (
                <button
                  className="dl-sozlesme-btn"
                  onClick={() => handleSozlesmeYap(daire)}
                >
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1={16} y1={13} x2={8} y2={13}/><line x1={16} y1={17} x2={8} y2={17}/><polyline points="10 9 9 9 8 9"/></svg>
                  Sözleşme
                </button>
              )}
              {/* Three-dot menu */}
              <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                <button
                  className="dl-three-dot"
                  onClick={() => setOpenMenuId(openMenuId === daire.daireID ? null : daire.daireID)}
                  title="İşlemler"
                >
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor"><circle cx={12} cy={5} r={1.8}/><circle cx={12} cy={12} r={1.8}/><circle cx={12} cy={19} r={1.8}/></svg>
                </button>
                {openMenuId === daire.daireID && (
                  <div className="dl-ctx-menu">
                    <div className="dl-ctx-item" onClick={() => { setModalDaire(daire); setOpenMenuId(null); }}>
                      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx={12} cy={12} r={3}/></svg>
                      {t('btn.daireDetayi')}
                    </div>
                    {['Sözleşme', 'Rezerve', 'ArsaSahibi', 'Taşeron'].includes(daire.daireDurumu) && (
                      <>
                        <div className="dl-divider" />
                        <div className="dl-ctx-item" style={{ color: '#2563eb' }} onClick={() => { openSozlesmeOzet(daire); setOpenMenuId(null); }}>
                          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1={16} y1={13} x2={8} y2={13}/><line x1={16} y1={17} x2={8} y2={17}/></svg>
                          {t('btn.sozlesmeOzeti')}
                        </div>
                      </>
                    )}
                    <div className="dl-divider" />
                    {daire.daireDurumu === 'Satışta' && (
                      <div className="dl-ctx-item" style={{ color: '#15803d' }} onClick={() => handleSozlesmeYap(daire)}>
                        <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        {t('btn.sozlesmeOlustur')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      )}

      {/* ── PAGINATION (tablo only) ──────────────────────────── */}
      {viewMode === 'tablo' && filteredDaireler.length > itemsPerPage && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 28 }}>
          <button
            onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
            style={{
              padding: '7px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0',
              background: '#fff', color: currentPage === 1 ? '#cbd5e1' : '#475569',
              fontSize: 12, fontWeight: 600, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            }}>«</button>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
            style={{
              padding: '7px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0',
              background: '#fff', color: currentPage === 1 ? '#cbd5e1' : '#475569',
              fontSize: 12, fontWeight: 600, cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            }}>‹</button>
          {Array.from({ length: getTotalPages() }, (_, i) => i + 1)
            .filter(p => p === 1 || p === getTotalPages() || Math.abs(p - currentPage) <= 2)
            .map((p, i, arr) => (
              <React.Fragment key={p}>
                {i > 0 && arr[i - 1] < p - 1 && (
                  <span style={{ color: '#cbd5e1', fontSize: 12, padding: '0 4px' }}>…</span>
                )}
                <button
                  onClick={() => setCurrentPage(p)}
                  style={{
                    width: 36, height: 36, borderRadius: 9,
                    border: p === currentPage ? 'none' : '1.5px solid #e2e8f0',
                    background: p === currentPage ? '#0f172a' : '#fff',
                    color: p === currentPage ? '#fff' : '#475569',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}>{p}</button>
              </React.Fragment>
            ))
          }
          <button
            onClick={() => setCurrentPage(p => Math.min(getTotalPages(), p + 1))} disabled={currentPage === getTotalPages()}
            style={{
              padding: '7px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0',
              background: '#fff', color: currentPage === getTotalPages() ? '#cbd5e1' : '#475569',
              fontSize: 12, fontWeight: 600, cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer',
            }}>›</button>
          <button
            onClick={() => setCurrentPage(getTotalPages())} disabled={currentPage === getTotalPages()}
            style={{
              padding: '7px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0',
              background: '#fff', color: currentPage === getTotalPages() ? '#cbd5e1' : '#475569',
              fontSize: 12, fontWeight: 600, cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer',
            }}>»</button>
        </div>
      )}

      {/* ── DAIRE DETAIL MODAL ───────────────────── */}
      {modalDaire && (
        <div className="dl-modal-overlay" onClick={() => setModalDaire(null)}>
          <div className="dl-modal" style={{ maxWidth: 1100, ...(dlDetayTab === 'gorunum' ? { height: '90vh', display: 'flex', flexDirection: 'column' } : {}) }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="dl-modal-header">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.04em' }}>
                      Daire {modalDaire.daireNo}
                    </span>
                    {getDurumBadge(modalDaire.daireDurumu)}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
                    {modalDaire.proje?.projeAdi || '—'}
                  </span>
                </div>
                {/* Tabs */}
                <div style={{ display: 'flex', gap: 0, marginTop: 12, marginLeft: -2 }}>
                  {(['galeri', 'gorunum'] as const).map(tab => (
                    <button key={tab}
                      onClick={async () => {
                        setDlDetayTab(tab);
                        if (tab === 'gorunum' && modalDaire.projeID && dlGorunumlar.length === 0) {
                          setDlGorunumLoading(true);
                          try {
                            const res = await axiosInstance.get(`/api/ProjeGorunumleri?projeID=${modalDaire.projeID}`);
                            setDlGorunumlar((res.data as DLGVGorunum[]).filter(g => g.daireler.some(d => d.daireID === modalDaire.daireID)));
                          } catch { /* sessiz */ }
                          finally { setDlGorunumLoading(false); }
                        }
                      }}
                      style={{
                        padding: '7px 16px', fontSize: 12.5, fontWeight: 600,
                        border: 'none', background: 'none', cursor: 'pointer',
                        borderBottom: dlDetayTab === tab ? '2px solid #e67d21' : '2px solid transparent',
                        color: dlDetayTab === tab ? '#e67d21' : '#64748b',
                        transition: 'color 0.15s',
                      }}
                    >
                      {tab === 'galeri'
                        ? <><i className="fas fa-images" style={{ marginRight: 5 }}></i>Galeri &amp; Detaylar</>
                        : <><i className="fas fa-layer-group" style={{ marginRight: 5 }}></i>Görünümler</>}
                    </button>
                  ))}
                </div>
              </div>
              <button className="dl-close-btn" onClick={() => setModalDaire(null)}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1={18} y1={6} x2={6} y2={18}/><line x1={6} y1={6} x2={18} y2={18}/></svg>
              </button>
            </div>

            {/* Body */}
            {/* Body - EP style 2-column layout */}
            <div style={{ display: dlDetayTab === 'gorunum' ? 'flex' : 'flex', flex: 1, overflow: 'hidden', minHeight: dlDetayTab === 'gorunum' ? 0 : undefined, flexDirection: dlDetayTab === 'gorunum' ? 'column' : undefined }}>

              {/* ── GÖRÜNÜMLER TAB ── */}
              {dlDetayTab === 'gorunum' && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                  {dlGorunumLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#64748b', flex: 1 }}>
                      <i className="fas fa-spinner fa-spin"></i> Görünümler yükleniyor...
                    </div>
                  ) : dlGorunumlar.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#94a3b8', flex: 1 }}>
                      <i className="fas fa-layer-group" style={{ fontSize: 36 }}></i>
                      <span style={{ fontSize: 14 }}>Bu daire için tanımlanmış görünüm bulunamadı.</span>
                    </div>
                  ) : (() => {
                    const g = dlGorunumlar[dlGorunumSelectedIdx] ?? dlGorunumlar[0];
                    const gd = g.daireler.find(d => d.daireID === modalDaire.daireID);
                    const durumColors: Record<string, string> = { 'Satışta': '#22c55e', 'Rezerve': '#f59e0b', 'Sözleşme': '#3b82f6' };
                    const dotColor = durumColors[modalDaire.daireDurumu] || '#94a3b8';
                    return (
                      <>
                        {dlGorunumlar.length > 1 && (
                          <div style={{ flexShrink: 0, padding: '8px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto' }}>
                            {dlGorunumlar.length <= 5
                              ? dlGorunumlar.map((gr, i) => (
                                  <button key={gr.gorunumID}
                                    onClick={() => setDlGorunumSelectedIdx(i)}
                                    style={{
                                      padding: '5px 14px', fontSize: 12.5, fontWeight: 600,
                                      border: dlGorunumSelectedIdx === i ? '1.5px solid #e67d21' : '1.5px solid #e2e8f0',
                                      borderRadius: 20, background: dlGorunumSelectedIdx === i ? '#fff4ea' : '#fff',
                                      color: dlGorunumSelectedIdx === i ? '#e67d21' : '#64748b',
                                      cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                                    }}
                                  >{gr.gorunumAdi}</button>
                                ))
                              : (
                                <select value={dlGorunumSelectedIdx} onChange={e => setDlGorunumSelectedIdx(Number(e.target.value))}
                                  style={{ fontSize: 13, padding: '5px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer' }}>
                                  {dlGorunumlar.map((gr, i) => <option key={gr.gorunumID} value={i}>{gr.gorunumAdi}</option>)}
                                </select>
                              )
                            }
                          </div>
                        )}
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#0f172a', minHeight: 0 }}>
                          <div style={{ position: 'relative', aspectRatio: '4/3', maxHeight: '100%', maxWidth: '100%', flexShrink: 0 }}>
                          {g.arkaplanGorsel
                            ? <img src={`${API_BASE_URL}/api/ProjeGorunumleri/gorsel/${g.arkaplanGorsel}`} alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }} />
                            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569', flexDirection: 'column', gap: 8, background: '#1e293b' }}>
                                <i className="fas fa-image" style={{ fontSize: 40 }}></i>
                                <span style={{ fontSize: 13 }}>Arka plan yok</span>
                              </div>
                          }
                          {gd && gd.rxMin != null && gd.ryMin != null && gd.rxMax != null && gd.ryMax != null && (
                            <div style={{
                              position: 'absolute',
                              left: `${gd.rxMin}%`, top: `${gd.ryMin}%`,
                              width: `${gd.rxMax! - gd.rxMin!}%`, height: `${gd.ryMax! - gd.ryMin!}%`,
                              border: `2px solid ${dotColor}`,
                              borderRadius: 4, boxSizing: 'border-box',
                              background: dlGorunumHovered === g.gorunumID ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.08)',
                              transition: 'background 0.15s', zIndex: 6,
                            }} />
                          )}
                          {g.daireler.filter(d => d.daireID !== modalDaire.daireID).map(d => (
                            <div key={d.daireID} style={{
                              position: 'absolute', left: `${d.xYuzde}%`, top: `${d.yYuzde}%`,
                              transform: 'translate(-50%,-50%)', width: 22, height: 22, borderRadius: '50%',
                              background: 'rgba(148,163,184,0.55)', border: '2px solid rgba(255,255,255,0.5)', zIndex: 7,
                            }} />
                          ))}
                          {gd && (
                            <div style={{
                              position: 'absolute', left: `${gd.xYuzde}%`, top: `${gd.yYuzde}%`,
                              transform: 'translate(-50%,-50%)',
                              width: 36, height: 36, borderRadius: '50%',
                              background: dotColor, border: '3px solid rgba(255,255,255,0.9)',
                              boxShadow: `0 0 0 2px ${dotColor}, 0 4px 12px rgba(0,0,0,0.4)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 8, fontWeight: 800, color: '#fff', zIndex: 10, cursor: 'default',
                            }}
                              onMouseEnter={() => setDlGorunumHovered(g.gorunumID)}
                              onMouseLeave={() => setDlGorunumHovered(null)}
                            >
                              {modalDaire.daireNo.slice(0, 4)}
                            </div>
                          )}
                          <div style={{ position: 'absolute', bottom: 14, left: 14, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12.5, fontWeight: 600, padding: '5px 12px', borderRadius: 20, backdropFilter: 'blur(6px)', zIndex: 12 }}>
                            <i className="fas fa-layer-group" style={{ marginRight: 6, opacity: 0.8 }}></i>{g.gorunumAdi}
                          </div>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Left: Gallery + Açıklama + Belgeler */}
              <div style={{ flex: '0 0 52%', overflowY: 'auto', padding: '20px 24px', borderRight: '1px solid #f1f5f9', display: dlDetayTab === 'gorunum' ? 'none' : 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Gallery */}
                {galeriGorseller.length > 0 ? (
                  <div>
                    <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#f1f5f9' }}>
                      <img
                        src={`${API_BASE_URL}/api/SunumBelgeleri/download/${galeriGorseller[galeriSlideIndex]?.sunumBelgeId}`}
                        alt={galeriGorseller[galeriSlideIndex]?.aciklama || ''}
                        style={{ width: '100%', maxHeight: 300, objectFit: 'cover', display: 'block' }}
                      />
                      {galeriGorseller[galeriSlideIndex]?.aciklama && (
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(0,0,0,0.6))', color: '#fff', padding: '18px 14px 8px', fontSize: 12, fontWeight: 600 }}>
                          {galeriGorseller[galeriSlideIndex].aciklama}
                        </div>
                      )}
                      {galeriGorseller.length > 1 && (
                        <>
                          <button onClick={() => setGaleriSlideIndex(i => i <= 0 ? galeriGorseller.length - 1 : i - 1)}
                            style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                            <i className="fas fa-chevron-left" style={{ fontSize: 12 }}></i>
                          </button>
                          <button onClick={() => setGaleriSlideIndex(i => i >= galeriGorseller.length - 1 ? 0 : i + 1)}
                            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                            <i className="fas fa-chevron-right" style={{ fontSize: 12 }}></i>
                          </button>
                        </>
                      )}
                      <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 9999 }}>
                        {galeriSlideIndex + 1} / {galeriGorseller.length}
                      </div>
                    </div>
                    {galeriGorseller.length > 1 && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, overflowX: 'auto', paddingBottom: 4 }}>
                        {galeriGorseller.map((foto, i) => (
                          <div key={foto.sunumBelgeId}
                            onClick={() => setGaleriSlideIndex(i)}
                            style={{ width: 58, height: 46, flexShrink: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: i === galeriSlideIndex ? '2px solid #cd6906' : '2px solid transparent', opacity: i === galeriSlideIndex ? 1 : 0.6, transition: 'all 0.15s' }}>
                            <img src={`${API_BASE_URL}/api/SunumBelgeleri/download/${foto.sunumBelgeId}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ borderRadius: 12, background: '#f8fafc', border: '1.5px dashed #e2e8f0', padding: '48px 24px', textAlign: 'center', color: '#94a3b8' }}>
                    <i className="fas fa-image" style={{ fontSize: 36, marginBottom: 10, display: 'block' }}></i>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Görsel eklenmemiş</div>
                  </div>
                )}

                {/* Açıklama */}
                {modalDaire.aciklama && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className="fas fa-align-left" style={{ color: '#94a3b8', fontSize: 12 }}></i> Açıklama
                    </div>
                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{modalDaire.aciklama}</div>
                  </div>
                )}

                {/* Belgeler */}
                {galeriBelgeler.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className="fas fa-paperclip" style={{ color: '#94a3b8', fontSize: 12 }}></i> Belgeler
                    </div>
                    {galeriBelgeler.map(b => (
                      <div key={b.sunumBelgeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <i className="fas fa-file-alt" style={{ color: '#3b82f6', fontSize: 14 }}></i>
                        <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{b.aciklama || b.sunumBelgeAdi || 'Belge'}</span>
                        <a href={`${API_BASE_URL}/api/SunumBelgeleri/download/${b.sunumBelgeId}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600, textDecoration: 'none' }}>
                          <i className="fas fa-download" style={{ marginRight: 3 }}></i>İndir
                        </a>
                      </div>
                    ))}
                  </div>
                )}

                {/* Videolar */}
                {galeriVideolar.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className="fas fa-video" style={{ color: '#94a3b8', fontSize: 12 }}></i> Videolar
                    </div>
                    {galeriVideolar.map(v => (
                      <div key={v.sunumBelgeId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                        onClick={() => setShowVideoModal(v)}>
                        <i className="fas fa-play-circle" style={{ color: '#dc2626', fontSize: 16 }}></i>
                        <span style={{ flex: 1, fontSize: 12.5, color: '#334155', fontWeight: 500 }}>{v.aciklama || 'Video'}</span>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>Oynat →</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Sidebar */}
              <div style={{ flex: '0 0 48%', overflowY: 'auto', padding: '20px 24px', display: dlDetayTab === 'gorunum' ? 'none' : 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Fiyat Kartı */}
                {modalDaire.listeFiyati != null ? (
                  <div style={{ background: '#fff8f0', borderRadius: 14, padding: '18px 20px', border: '1px solid #fed7aa' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#cd6906', letterSpacing: '-0.02em', lineHeight: 1 }}>
                        {modalDaire.listeFiyati.toLocaleString('tr-TR')} ₺
                      </div>
                      {getDurumBadge(modalDaire.daireDurumu)}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const }}>
                    {getDurumBadge(modalDaire.daireDurumu)}
                  </div>
                )}

                {/* Detaylar */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
                    <i className="fas fa-list-ul" style={{ color: '#94a3b8', fontSize: 12 }}></i> Detaylar
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {([
                      { icon: 'fa-building', label: 'Proje', value: modalDaire.proje?.projeAdi },
                      { icon: 'fa-layer-group', label: 'Blok', value: modalDaire.blok?.blokAdi },
                      { icon: 'fa-th-large', label: 'Daire Tipi', value: modalDaire.daireTipi?.tip },
                      { icon: 'fa-sort-amount-up', label: 'Kat', value: modalDaire.kat?.katAdi },
                      { icon: 'fa-compass', label: 'Cephe', value: modalDaire.cephe?.cepheAdi },
                      { icon: 'fa-mountain', label: 'Manzara', value: modalDaire.manzara?.manzaraAdi },
                      { icon: 'fa-ruler-combined', label: 'Net m²', value: modalDaire.netM2 != null ? `${modalDaire.netM2} m²` : null },
                      { icon: 'fa-ruler', label: 'Brüt m²', value: modalDaire.brutM2 != null ? `${modalDaire.brutM2} m²` : null },
                      { icon: 'fa-tag', label: '₺/m²', value: (modalDaire.netM2 != null && modalDaire.netM2 > 0 && modalDaire.listeFiyati != null) ? `${Math.round(modalDaire.listeFiyati / modalDaire.netM2).toLocaleString('tr-TR')} ₺/m²` : null },
                    ] as { icon: string; label: string; value?: string | null }[]).filter(item => item.value).map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f8fafc' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#94a3b8', fontWeight: 600 }}>
                          <i className={`fas ${item.icon}`} style={{ width: 14, textAlign: 'center' as const, color: '#cd6906', fontSize: 12 }}></i>
                          {item.label}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{item.value}</span>
                      </div>
                    ))}
                    {modalDaire.daireDurumu === 'Sözleşme' && sozlesmeFiyatlari[modalDaire.daireID] != null && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f8fafc' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#94a3b8', fontWeight: 600 }}>
                          <i className="fas fa-handshake" style={{ width: 14, textAlign: 'center' as const, color: '#2563eb', fontSize: 12 }}></i>
                          Satış Fiyatı
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>₺{sozlesmeFiyatlari[modalDaire.daireID].toLocaleString('tr-TR')}</span>
                      </div>
                    )}
                    {['Sözleşme', 'Rezerve', 'ArsaSahibi', 'Taşeron'].includes(modalDaire.daireDurumu) && sozlesmeMusterileri[modalDaire.daireID] && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f8fafc' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#94a3b8', fontWeight: 600 }}>
                          <i className="fas fa-user" style={{ width: 14, textAlign: 'center' as const, color: '#8b5cf6', fontSize: 12 }}></i>
                          Müşteri
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{sozlesmeMusterileri[modalDaire.daireID]}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Daire Özellikleri */}
                {modalDaireOzellikler.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className="fas fa-tags" style={{ color: '#94a3b8', fontSize: 12 }}></i> Daire Özellikleri
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 7 }}>
                      {modalDaireOzellikler.map((oz, idx) => (
                        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 9999, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: 12, fontWeight: 600, color: '#334155' }}>
                          <i className="fas fa-check" style={{ color: '#059669', fontSize: 10 }}></i>
                          {oz.ozellik}: {oz.deger}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Proje Özellikleri */}
                {modalProjeOzellikler.length > 0 && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 7 }}>
                      <i className="fas fa-tags" style={{ color: '#94a3b8', fontSize: 12 }}></i> Proje Özellikleri
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 7 }}>
                      {modalProjeOzellikler.map((oz, idx) => (
                        <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 9999, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 12, fontWeight: 600, color: '#15803d' }}>
                          <i className="fas fa-check" style={{ color: '#059669', fontSize: 10 }}></i>
                          {oz.ozellik}: {oz.deger}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sunuma Ekle — sadece Satışta dairelerde */}
                {modalDaire.daireDurumu === 'Satışta' && (
                  <div>
                    <button
                      onClick={openDlSunumaEkle}
                      style={{ width: '100%', padding: '11px 0', border: 'none', borderRadius: 10, background: '#cd6906', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    >
                      <i className="fas fa-plus" style={{ fontSize: 13 }} />
                      Sunuma Ekle
                    </button>
                    {dlSunumModal && (
                      <div style={{ marginTop: 10, border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                        <div style={{ padding: '10px 14px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>
                            <i className="fas fa-list" style={{ marginRight: 6, color: '#cd6906' }} />
                            Aktif Sunumlar
                            <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8', marginLeft: 6 }}>Taslak / Devam Ediyor</span>
                          </span>
                          <button onClick={() => setDlSunumModal(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 15, lineHeight: 1 }}>✕</button>
                        </div>
                        <div style={{ maxHeight: 220, overflowY: 'auto', padding: '8px 10px 10px' }}>
                          {dlSunumYukleniyor ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                              <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Yükleniyor...
                            </div>
                          ) : dlSunumlar.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                              Taslak veya devam eden sunum bulunamadı.
                            </div>
                          ) : dlSunumlar.map(s => {
                            const ekli = dlSunumEklendi.has(s.sunumID);
                            const isEkleniyor = dlSunumEkleniyor === s.sunumID;
                            const durumRenk: Record<string, { bg: string; col: string }> = {
                              'Taslak': { bg: '#f1f5f9', col: '#475569' },
                              'Devam Ediyor': { bg: '#eff6ff', col: '#1d4ed8' },
                            };
                            const dk = durumRenk[s.durum] ?? { bg: '#f1f5f9', col: '#475569' };
                            return (
                              <div key={s.sunumID} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, background: '#fff', border: '1px solid #e2e8f0', marginBottom: 6 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {s.baslik || s.cariAdSoyad || s.takipNo}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {s.cariAdSoyad && <span>{s.cariAdSoyad}</span>}
                                    <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: dk.bg, color: dk.col }}>{s.durum}</span>
                                    <span style={{ color: '#94a3b8' }}>{s.daireler.length} daire</span>
                                  </div>
                                </div>
                                {ekli ? (
                                  <span style={{ padding: '4px 10px', borderRadius: 8, background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                    <i className="fas fa-check" style={{ marginRight: 4 }} />Mevcut
                                  </span>
                                ) : (
                                  <button onClick={() => handleDlSunumaEkle(s.sunumID)} disabled={isEkleniyor}
                                    style={{ padding: '5px 12px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: isEkleniyor ? 'not-allowed' : 'pointer', flexShrink: 0, opacity: isEkleniyor ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {isEkleniyor ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-plus" />}
                                    Ekle
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="dl-modal-footer">
              <button
                onClick={() => setModalDaire(null)}
                style={{
                  padding: '8px 20px', borderRadius: 10,
                  border: '1.5px solid #e2e8f0', background: '#f8fafc',
                  fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer',
                }}
              >
                Kapat
              </button>
              {modalDaire.daireDurumu === 'Sat\u0131\u015fta' && (
                <button
                  className="dl-sozlesme-btn"
                  onClick={() => { handleSozlesmeYap(modalDaire); setModalDaire(null); }}
                >
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Sözleşme
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── SÖZLEŞME ÖZETİ MODAL ───────────────── */}
      {sozlesmeOzetDaire && (
        <div className="dl-modal-overlay" onClick={() => setSozlesmeOzetDaire(null)}>
          <div className="dl-modal" style={{ maxWidth: 860 }} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="dl-modal-header">
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginBottom: 4 }}>
                  {sozlesmeOzetDaire.proje?.projeAdi || '—'} • Daire {sozlesmeOzetDaire.daireNo}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.04em' }}>
                    Sözleşme Özeti
                  </span>
                  {getDurumBadge(sozlesmeOzetDaire.daireDurumu)}
                </div>
              </div>
              <button className="dl-close-btn" onClick={() => setSozlesmeOzetDaire(null)}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1={18} y1={6} x2={6} y2={18}/><line x1={6} y1={6} x2={18} y2={18}/></svg>
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '20px 28px' }}>
              {sozlesmeOzetLoading ? (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', animation: 'dl-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>{t('msg.yukleniyor')}</span>
                  <style>{`@keyframes dl-spin { to { transform: rotate(360deg); } }`}</style>
                </div>
              ) : !sozlesmeOzet ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
                  Sözleşme bilgisi bulunamadı.
                </div>
              ) : (
                <>
                  {/* Müşteri & Sözleşme Bilgileri */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px', marginBottom: 20 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginBottom: 3 }}>Müşteri</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{sozlesmeOzet.cari?.adiSoyadi || '—'}</div>
                      {sozlesmeOzet.cari?.telefon1 && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{sozlesmeOzet.cari.telefon1}</div>}
                      {sozlesmeOzet.cari?.ePosta && <div style={{ fontSize: 12, color: '#64748b' }}>{sozlesmeOzet.cari.ePosta}</div>}
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginBottom: 3 }}>Daire</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                        {sozlesmeOzet.daire?.daireNo || sozlesmeOzetDaire.daireNo} • {sozlesmeOzetDaire.blok?.blokAdi || '—'} • {sozlesmeOzetDaire.kat?.katAdi || '—'}
                      </div>
                      {(sozlesmeOzetDaire.netM2 || sozlesmeOzetDaire.brutM2) && (
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                          {sozlesmeOzetDaire.netM2 && `Net: ${sozlesmeOzetDaire.netM2} m²`}
                          {sozlesmeOzetDaire.netM2 && sozlesmeOzetDaire.brutM2 && ' / '}
                          {sozlesmeOzetDaire.brutM2 && `Brüt: ${sozlesmeOzetDaire.brutM2} m²`}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginBottom: 3 }}>Takip No</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{sozlesmeOzet.sozlesmeTakipNo || '—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginBottom: 3 }}>Sözleşme Tarihi</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                        {sozlesmeOzet.sozlesmeTarihi ? new Date(sozlesmeOzet.sozlesmeTarihi).toLocaleDateString('tr-TR') : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginBottom: 3 }}>Sözleşme Fiyatı</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#059669' }}>
                        ₺{sozlesmeOzet.sozlesmeFiyati?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', marginBottom: 3 }}>Planlanan Ödeme</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>
                        ₺{sozlesmeOzet.planlananOdeme?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '—'}
                      </div>
                    </div>
                  </div>

                  {/* Ödeme Tablosu */}
                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      Ödeme Planı
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', background: '#f1f5f9', borderRadius: 12, padding: '2px 8px' }}>
                        {sozlesmeOdemeler.length} kayıt
                      </span>
                    </div>
                    {sozlesmeOdemeler.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 12.5, background: '#f8fafc', borderRadius: 10 }}>
                        Ödeme kaydı bulunamadı.
                      </div>
                    ) : (
                      <div style={{ maxHeight: 260, overflowY: 'auto', borderRadius: 10, border: '1.5px solid #f1f5f9' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.3px', borderBottom: '1.5px solid #f1f5f9' }}>Vade Tarihi</th>
                              <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.3px', borderBottom: '1.5px solid #f1f5f9' }}>{t('label.tutar')}</th>
                              <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.3px', borderBottom: '1.5px solid #f1f5f9' }}>{t('label.tur')}</th>
                              <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#64748b', fontSize: 11, letterSpacing: '0.3px', borderBottom: '1.5px solid #f1f5f9' }}>Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sozlesmeOdemeler.map((odeme: any, idx: number) => {
                              const durumRenk: Record<string, { bg: string; color: string }> = {
                                'Odendi': { bg: '#dcfce7', color: '#15803d' },
                                'Bekliyor': { bg: '#fef9c3', color: '#a16207' },
                                'Gecikmiş': { bg: '#fee2e2', color: '#dc2626' },
                                'Iptal': { bg: '#f1f5f9', color: '#64748b' },
                              };
                              const d = durumRenk[odeme.odemeDurumu] || { bg: '#f1f5f9', color: '#475569' };
                              return (
                                <tr key={odeme.odemeID || idx} style={{ borderBottom: '1px solid #f8fafc' }}>
                                  <td style={{ padding: '8px 12px', color: '#334155', fontWeight: 600 }}>
                                    {odeme.vadeTarihi ? new Date(odeme.vadeTarihi).toLocaleDateString('tr-TR') : '—'}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                                    ₺{odeme.miktar?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0'}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center', color: '#64748b', fontWeight: 500 }}>
                                    {odeme.odemeTuru || '—'}
                                  </td>
                                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                    <span style={{
                                      display: 'inline-block', padding: '2px 10px', borderRadius: 9999,
                                      fontSize: 11, fontWeight: 700, background: d.bg, color: d.color,
                                    }}>
                                      {odeme.odemeDurumu === 'Odendi' ? 'Ödendi' : odeme.odemeDurumu === 'Iptal' ? 'İptal' : odeme.odemeDurumu || '—'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: '#f8fafc', borderTop: '1.5px solid #e2e8f0' }}>
                              <td style={{ padding: '8px 12px', fontWeight: 700, color: '#0f172a', fontSize: 12 }}>Toplam</td>
                              <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 800, color: '#059669', fontSize: 13 }}>
                                ₺{sozlesmeOdemeler.reduce((s: number, o: any) => s + (o.miktar || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="dl-modal-footer">
              <button
                onClick={() => setSozlesmeOzetDaire(null)}
                style={{
                  padding: '8px 20px', borderRadius: 10,
                  border: '1.5px solid #e2e8f0', background: '#f8fafc',
                  fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer',
                }}
              >
                Kapat
              </button>
              {sozlesmeOzet?.sozlesmeTakipNo && (
                <button
                  style={{
                    padding: '8px 20px', borderRadius: 10, border: 'none',
                    background: '#cd6906', color: '#fff',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                  onClick={() => { navigate(`/sozlesme-detay/${sozlesmeOzet.sozlesmeTakipNo}`); setSozlesmeOzetDaire(null); }}
                >
                  <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1={10} y1={14} x2={21} y2={3}/></svg>
                  Detaya Git
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="dl-modal-overlay" onClick={() => setShowVideoModal(null)} style={{ zIndex: 1060 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#000', borderRadius: 12, maxWidth: 720, width: '90%', overflow: 'hidden', position: 'relative' }}>
            <button onClick={() => setShowVideoModal(null)}
              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>×</button>
            {showVideoModal.sunumBelgeAdi && (
              <div style={{ width: '100%' }}
                dangerouslySetInnerHTML={{ __html: showVideoModal.sunumBelgeAdi }} />
            )}
            {showVideoModal.aciklama && (
              <div style={{ padding: '8px 14px', color: '#fff', fontSize: 13, fontWeight: 600 }}>{showVideoModal.aciklama}</div>
            )}
          </div>
        </div>
      )}
    </div>

  );
};

export default DairelerListe;